// The online IDE (docs/ONLINE_IDE_FEASIBILITY.md, Phases 1 and 2): projects of
// sources and media, kept in the browser or in a folder on the user's disk
// (projects.js), compiled in a web worker (compiler/worker.js) and run in a
// frame on another site (runner.js), with the help beside them (help.js), a
// debugger (debugger.js) and nothing on the server.
//
// URL options: ?run=<origin> names the run frame's site, with {n} standing for
// the number of a fresh site (runner.js). By default, served from localhost,
// the run frames are on r0.localhost, r1.localhost, ..., which are sites of
// their own, so one local server serves both; served from 127.0.0.1, they are
// on localhost, then r1.localhost, ... Passkeys need a host name, so accounts
// work on localhost, not 127.0.0.1. Served from any other host without a
// server (a static host such as GitHub Pages), they are on the page's own
// site.
// ?example=<name> opens a help example. A #title=...&code=... fragment opens a
// shared program (share.js) as a new project, in the full-width editor.
// With either, as the website's links have them, ?wide=1 gives the program
// the full-width editor too, and ?live=1 turns Live on and runs it as soon as
// the compiler is ready. Live is not remembered from such a link.
//
// Automated checks use window.b3dIde (the last run's timings and outcome) and
// the b3dIde* functions at the end.
//
// Blip, the coding helper (blip/panel.js, docs/BLIP_HELPER_DESIGN.md), joins
// in when the IDE's server offers him: he edits files through the editor,
// compiles, and tries the program with a test run (checkRun below).

import { parseManifest } from "./manifest.js";
import { createEditor, isKeyword, constantOf } from "./editor.js";
import { Runner, earlierWebBuilds, packAssets, skipAsset } from "./runner.js";
import { numberSpans, numberChange } from "./livenumbers.js";
import { encodeShare, decodeShare } from "./share.js";
import { ProjectStore, guessMainFile, findMainFile, normalisePath } from "./projects.js";
import { FileTree, chosenFolder } from "./filetree.js";
import { Help } from "./help.js";
import { readZip } from "./zip.js";
import { download, projectZip, startInputs, webBuildZip, webPage } from "./export.js";
import { Cloud } from "./cloud.js";
import { setupAccount } from "./account.js";
import { setupLibrary } from "./library.js";
import { setupDebugger } from "./debugger.js";
import { setupSplitters } from "./splitters.js";
import { FileTabs, HELP, MODULES } from "./tabs.js";
import { SymbolPane, includes, index } from "./symbols.js";
import { FindInFiles } from "./findfiles.js";
import { SITE_VERSION, versioned } from "./site.js";
import { WorldEditor, temporaryFile, playWithBehaviours } from "./worldeditor.js";
import { ModelViewer, MODEL } from "./modelviewer.js";
import { setupBlip } from "./blip/panel.js";
import { setupModules } from "./modules.js";
import { setupBuddies } from "./buddies/panel.js";
import { OnlineTest, NETWORKS as ONLINE_NETWORKS, BOTS as ONLINE_BOTS, describe as describeOnlineTest,
  SERVICE as ONLINE_SERVICE } from "./onlinetest.js";
import { PROTOCOL as SESSION_PROTOCOL, shareWorld } from "./buddies/world.js";

const $ = (id) => document.getElementById(id);
const params = new URLSearchParams(location.search);
const state = window.b3dIde = { ready: false, maxStallMs: 0, runs: 0, lastRun: null, notResponding: false };
// True while a program, not the World Editor, runs (setRunning).
let programRunning = false;
// Blip's controls (blip/panel.js), when the server offers him.
let blip = null;
// The Modules tab (modules.js), when the server has a module library.
let modules = null;
// Buddies, working on a program together, live (buddies/panel.js), when the
// server offers them.
let buddies = null;
// While the World Editor runs in a buddy session, the relay that shares its
// world when the session has it (buddies/world.js, syncSharedWorld).
let sharedWorld = null;
const WORLD_WAITS = "The World Editor opens once the program ends: on the web it cannot show a running program's world.";
const LAST_PROJECT = "b3d-ide:project", DEBUG = "b3d-ide:debug", LIVE = "b3d-ide:live",
  BREAKPOINTS = "b3d-ide:breakpoints:", TABS = "b3d-ide:tabs:";
// index.html reads LAYOUT and CONSOLE too, to draw the layout before this script runs.
const LAYOUT = "b3d-ide:layout", FONT = "b3d-ide:font", CONSOLE = "b3d-ide:console";
const FONT_SIZE = "b3d-ide:font-size", LINE_NUMBERS = "b3d-ide:line-numbers";
// Program > Run online test's last choices, as {bots, network}.
const ONLINE_TEST = "b3d-ide:online-test";
const remember = {
  get(key) { try { return localStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { localStorage.setItem(key, value); } catch {} },
};

// This page's longest main-thread stall, from a heartbeat.
let lastBeat = performance.now();
setInterval(() => {
  const now = performance.now();
  state.maxStallMs = Math.max(state.maxStallMs, now - lastBeat - 100);
  lastBeat = now;
}, 100);

const status = (text) => { $("status").textContent = text; };

// The code font (index.html's --code-*): the Blitz font, the desktop editor's,
// drawn as the desktop draws it, each of its pixels a whole number of the
// screen's pixels, and about one CSS pixel, so that it stays sharp. Or else a
// modern font that scales smoothly (View > Blitz font).
const blitzFont = () => remember.get(FONT) !== "modern";
// The Windows IDE's three sizes, Small, Medium and Large (12, 20 and 30
// points there), in the same proportions; Medium is the size the code had
// before there were sizes.
const FONT_SIZES = [["Small", 0.75], ["Medium", 1], ["Large", 1.5]];
const MEDIUM = 1;
const fontSize = () => {
  const saved = remember.get(FONT_SIZE);
  const size = saved === null ? MEDIUM : Number(saved);
  return Number.isInteger(size) && size >= 0 && size < FONT_SIZES.length ? size : MEDIUM;
};
function setCodeFont() {
  const style = document.documentElement.style;
  const scale = FONT_SIZES[fontSize()][1];
  let size = `${13 * scale}px`;
  if (blitzFont()) {
    // Whole screen pixels for each of the font's: a size smaller or larger
    // than Medium's, as asked, unless the screen has no smaller one.
    const multiple = (factor) => Math.max(1, Math.round(devicePixelRatio * factor + 0.25));
    const medium = multiple(1);
    let pixels = multiple(scale);
    if (scale < 1 && pixels >= medium) pixels = medium - 1;
    if (scale > 1 && pixels <= medium) pixels = medium + 1;
    if (pixels >= 1) size = `${13 * pixels / devicePixelRatio}px`;
  }
  style.setProperty("--code-font", blitzFont() ? '"Blitz", "Cascadia Mono", Consolas, monospace' :
    '"Cascadia Mono", Consolas, "DejaVu Sans Mono", Menlo, monospace');
  style.setProperty("--code-size", size);
  style.setProperty("--code-line", blitzFont() ? size : `${Math.round(20 * scale)}px`);
  $("menu-blitz-font").setAttribute("aria-checked", String(blitzFont()));
  $("menu-font-smaller").disabled = fontSize() === 0;
  $("menu-font-larger").disabled = fontSize() === FONT_SIZES.length - 1;
  $("menu-font-reset").disabled = fontSize() === MEDIUM;
}
setCodeFont();

// Notices above the editor, each with optional buttons.
const notice = (text, buttons = [], kind = "") => {
  const element = document.createElement("div");
  element.className = "notice " + kind;
  const message = document.createElement("span");
  message.textContent = text;
  element.append(message);
  const close = () => element.remove();
  for (const [label, action] of buttons) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.addEventListener("click", () => { close(); action(); });
    element.append(button);
  }
  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.className = "dismiss";
  dismiss.title = "Dismiss";
  dismiss.setAttribute("aria-label", "Dismiss");
  dismiss.textContent = "×";
  dismiss.addEventListener("click", close);
  element.append(dismiss);
  $("notices").append(element);
  return element;
};
const clearNotices = (kind) => {
  for (const element of document.querySelectorAll(`.notice.${kind}`)) element.remove();
};
// Asks before an action, as the desktop IDEs' message boxes do: true for the
// action's button, false for Cancel or Esc.
const ask = ({ title, message, detail = "", action }) => new Promise((resolve) => {
  const dialog = $("ask-dialog");
  $("ask-title").textContent = title;
  $("ask-message").textContent = message;
  $("ask-detail").textContent = detail;
  $("ask-detail").hidden = !detail;
  $("ask-action").textContent = action;
  dialog.returnValue = "";
  dialog.addEventListener("close", () => resolve(dialog.returnValue === "action"), { once: true });
  dialog.showModal();
});
// Runs an action, reporting what goes wrong.
const attempt = (what, action) => async (...rest) => {
  try {
    return await action(...rest);
  } catch (error) {
    console.error(error);
    notice(`${what}: ${error && error.message || error}`, [], "warning");
  }
};

// The console, which View > Console, Ctrl+` or its × hides. While the user
// has it hidden, it comes back when a program prints or a build has errors,
// until it is hidden again, or the next run starts.
const consoleElement = $("console");
const consoleView = {
  hidden: document.documentElement.classList.contains("console-hidden"),
  apply() {
    document.documentElement.classList.toggle("console-hidden", this.hidden);
    $("menu-console").setAttribute("aria-checked", String(!this.hidden));
    // The full-width editor's button and menu show whether the console is gone too.
    layout.apply();
  },
  // Shows or hides it, as the user asked; showing it ends its coming back.
  toggle(show = this.hidden) {
    this.hidden = !show;
    layout.beforeHelp = null;
    document.documentElement.classList.remove("console-shown");
    remember.set(CONSOLE, this.hidden ? "hidden" : "shown");
    this.apply();
    status(this.hidden ? "The console is hidden. It comes back when a program prints or a build has errors; " +
      "Ctrl+` shows it." : "The console is under the editor.");
  },
  // A program printed, or a build failed.
  reveal() {
    if (this.hidden) document.documentElement.classList.add("console-shown");
  },
  // A run starts: a console the user hid goes again.
  conceal() {
    document.documentElement.classList.remove("console-shown");
  },
};
const write = (text, kind = "") => {
  if (kind !== "note") consoleView.reveal();
  const line = document.createElement("div");
  if (kind) line.className = kind;
  line.textContent = text;
  consoleElement.append(line);
  if (consoleElement.childElementCount > 5000) consoleElement.firstElementChild.remove();
  consoleElement.scrollTop = consoleElement.scrollHeight;
  return line;
};
const writeLocation = (path, row, col, message) => {
  const line = write("", "error");
  const link = document.createElement("a");
  link.href = "#";
  link.textContent = `${path}:${row}${col ? ":" + col : ""}`;
  link.addEventListener("click", (event) => {
    event.preventDefault();
    showLocation(path, row, col || 1);
  });
  line.append(link, " " + message);
};
$("clear-console").addEventListener("click", () => consoleElement.replaceChildren());

// What this browser can do. The IDE opens in more browsers than can run
// programs, and says what is missing.
const canRun = typeof WebAssembly === "object" && typeof WebAssembly.Suspending === "function";
if (!canRun) {
  notice("This browser can edit and check programs, but cannot run them: running needs WebAssembly " +
    "JavaScript Promise Integration, in Chrome and Edge 137 or later, Firefox 153 or later, and Safari 27 or later.",
    [], "warning");
} else if (!navigator.gpu) {
  notice("This browser has no WebGPU, so programs run without 3D graphics.", [], "warning");
}

// The compiler and the runtime's commands.
const worker = new Worker(versioned(new URL("compiler/worker.js", import.meta.url)), { type: "module" });
const pending = new Map();
let nextBuild = 1;
worker.onmessage = ({ data }) => {
  const resolve = pending.get(data.id);
  pending.delete(data.id);
  if (resolve) resolve(data);
};
const compile = (files, main, mode, debug, live = false) => new Promise((resolve) => {
  const id = nextBuild++;
  pending.set(id, resolve);
  worker.postMessage({ type: "compile", id, files, main, mode: "extended", debug, live });
});
// A site download's start list (startList in compiler/compile.mjs), from
// what its build loads by name and its package.
const listStart = ({ loads, launchDirectory, assets }) => new Promise((resolve) => {
  const id = nextBuild++;
  pending.set(id, resolve);
  worker.postMessage({ type: "start-list", id, loads, launchDirectory, ...startInputs(assets) });
});

const [manifestText, exampleList, store, cloud] = await Promise.all([
  fetch(versioned("compiler/runtime.manifest")).then((response) => response.text()),
  fetch(versioned("help/examples.json")).then((response) => response.json()),
  ProjectStore.open(),
  // The server beside the site, when there is one (Phase 3).
  Cloud.connect(),
]);
// Changes to the project open are saved to the account (account.js).
store.changed = (changed) => account && account.changed(changed);
let account = null;
const commands = parseManifest(manifestText);
// StartJob is Extended syntax that the compiler reads (compiler/parser.cpp),
// not a runtime command, but it is written, hinted and looked up as one:
// blitzcc +k gives the desktop IDEs its syntax too.
commands.set("startjob", { name: "StartJob", returns: "%", extended: true, params: [
  { sigil: "", name: "worker_function" }, { sigil: "%", name: "input_bank", default: "0" },
  { sigil: "&", name: "tag", default: "0" }] });
const examples = exampleList.examples;
const firstExample = examples.find((example) => example.path === exampleList.first) || examples[0];
if (!store.persistent) {
  notice("This browser cannot keep files, so they last only until the page closes. Use File > Download files to keep them.",
    [], "warning");
}

// Files the editor opens as text; others are shown as pictures, sounds or
// their sizes. The compiler is given the text files, in which a program's
// Include files are.
const TEXT = /\.(bb|bbinc|inc|decls|txt|md|json|ini|cfg|csv|xml|html?|css|js|world|ui|recipe|b3shader|hlsl|wgsl|glsl|log)$/i;
const IMAGE = /\.(png|jpe?g|gif|bmp|webp|svg|ico)$/i;
const SOUND = /\.(wav|ogg|mp3|m4a|flac)$/i;

// The project, and the file shown.
let project = null;
const writes = new Map();       // path -> timer, for text waiting to be saved
let previewUrl = null;

const editor = createEditor({
  parent: $("editor"),
  commands,
  isExtended: () => true,
  lineNumbers: remember.get(LINE_NUMBERS) !== "off",
  onRun: () => runKey("F5"),
  onHelp: (word) => showHelpFor(word),
  onDebugKey: (key) => runKey(key),
  // Ctrl+I: Blip's panel.
  onBlip: () => {
    if (!blip) return false;
    blip.toggle();
    return true;
  },
  onBreakpoints: () => {
    saveBreakpoints();
    debugPane.breakpointsChanged();
    symbolsChanged();
  },
  onCursor: (row, column) => { $("cursor-status").textContent = `Row:${row} Col:${column}`; },
  onWide: () => {
    layout.toggle();
    return true;
  },
  // A number dragged or a colour picked goes live at once.
  onLiveEdit: () => liveChanged(true),
  onStatus: (text) => status(text),
  onChange: (path) => {
    if (!project || !path) return;
    clearTimeout(writes.get(path));
    writes.set(path, setTimeout(() => save(path), 400));
    liveChanged();
    symbolsChanged();
  },
});

// The screen's pixels change size with the browser's zoom, and from one
// screen to another, and the Blitz font with them.
const watchScale = () => matchMedia(`(resolution: ${devicePixelRatio}dppx)`).addEventListener("change", () => {
  setCodeFont();
  editor.refreshFont();
  watchScale();
}, { once: true });
watchScale();

// Saves an edited file, and marks a project made from an example as changed.
const save = attempt("The file could not be saved", async (path) => {
  clearTimeout(writes.get(path));
  writes.delete(path);
  const text = editor.texts().get(path);
  if (text === undefined || !project || !project.has(path)) return;
  await project.write(path, text);
  if (project.meta.example && !project.meta.modified) await project.update({ modified: true });
});
const saveAll = async () => {
  await Promise.all([...writes.keys()].map((path) => save(path)));
};
addEventListener("pagehide", () => { saveAll(); });

// Removes a file: the file list's Delete, after it asks, and Undo of a file
// Blip made.
const removeFile = attempt("The file could not be deleted", async (path) => {
  clearTimeout(writes.get(path));
  writes.delete(path);
  await project.remove(path);
  editor.close(path);
  const shown = tabs.active === path;
  tabs.remove(path);
  saveBreakpoints();
  if (tree.selected === path || shown) {
    const next = tabs.active || tabs.paths[0] || project.main || project.paths()[0];
    if (next) await openFile(next);
    else showHelpView();
  }
  tree.show(project);
  liveChanged();
});

const tree = new FileTree($("files"), {
  open: (path) => openFile(path),
  rename: attempt("The file could not be renamed", async (from, to) => {
    await saveAll();
    const renamed = await project.rename(from, to);
    editor.rename(from, renamed);
    tabs.rename(from, renamed);
    saveBreakpoints();
    tree.show(project, tree.selected === from ? renamed : tree.selected);
    liveChanged();
  }),
  remove: (path) => removeFile(path),
  setMain: attempt("The file could not be chosen", async (path) => {
    await project.update({ main: path, mode: "extended" });
    tree.show(project);
    symbolsChanged();
    status(`Run now starts ${path}.`);
    liveChanged();
  }),
  add: (files) => addFiles(files),
  editWorld: (path) => openWorldEditor(path),
  worldEditorWaits: () => programRunning,
});

// Shows a file: text in the editor, or a preview.
const openFile = attempt("The file could not be opened", async (path) => {
  if (!project || !project.has(path)) return;
  tabs.show(path);
  tree.select(path);
  clearNotices("world");
  if (/\.world$/i.test(path) && !document.body.classList.contains("tool-running") && !programRunning) {
    notice(`${path} is a world, which the World Editor edits.`,
      [["Edit in World Editor", () => openWorldEditor(path)]], "world");
  }
  if (TEXT.test(path)) {
    editor.open(path, editor.isOpen(path) ? "" : await project.text(path));
    showPreview(null);
    editor.focus();
  } else {
    showPreview(path);
  }
  if (buddies) buddies.fileShown(path);
});

// The open files' tabs (tabs.js), kept for each program.
const tabs = new FileTabs($("file-tabs"), {
  select: (path) => (path === HELP ? showHelpView() : path === MODULES ? showModulesView() : openFile(path)),
  close: (path) => closeTab(path),
  changed: () => {
    if (project) remember.set(TABS + project.id, JSON.stringify({ paths: tabs.paths, active: tabs.active }));
    if (tabs.active === HELP || tabs.active === MODULES) layout.enterHelp();
    else if (tabs.active) layout.leaveHelp();
    symbolsChanged();
  },
});
// The functions, types, labels and breakpoints of the program's files
// (symbols.js): the file Run starts and the files it Includes, then the
// other files open in tabs. They are read again a moment after a change.
const symbolPane = new SymbolPane({ tabs: $("symbol-tabs"), list: $("symbols"), findPanel: $("find-panel"),
  show: (path, line) => showLocation(path, line + 1, 1) });
// Find in Files (findfiles.js), in the pane's Find tab: the program's text
// files as the editor has them, all of them, or the file Run starts and the
// files it Includes, or the files open in tabs.
const findInFiles = new FindInFiles($("find-panel"), {
  files: async (lookIn) => {
    if (!project) return [];
    if (lookIn === "program") return (await programSources(false)).map(({ path, text }) => [path, text]);
    const opened = editor.texts();
    const found = [];
    for (const path of (lookIn === "open" ? tabs.paths : project.paths())) {
      if (TEXT.test(path) && project.has(path)) found.push([path, opened.get(path) ?? await project.text(path)]);
    }
    return found;
  },
  show: async (path, match) => {
    await openFile(path);
    editor.select(match.row, match.col, match.length);
  },
  // Files open in the editor change there, as one edit each that Ctrl+Z
  // undoes; the others are written.
  replace: async (changes) => {
    const failed = [];
    for (const { path, matches, replacement, text } of changes) {
      try {
        const ranges = matches.map(({ offset, length }) => ({ from: offset, to: offset + length }));
        if (editor.replaceRanges(path, ranges, replacement)) await save(path);
        else {
          await project.write(path, text);
          if (project.meta.example && !project.meta.modified) await project.update({ modified: true });
        }
      } catch (error) {
        failed.push({ path, message: String(error && error.message || error) });
      }
    }
    liveChanged();
    symbolsChanged();
    return failed;
  },
  ask,
  report: (text) => status(text),
});
// Edit > Find in files and Replace in files (Ctrl+Shift+F and Ctrl+Shift+R),
// as in the Windows IDE: the Find tab of the pane beside the editor, shown if
// it was hidden, with the editor's selection to find.
function showFindInFiles(replacing = false) {
  layout.showFind();
  symbolPane.select("find");
  findInFiles.focus(editorShown() ? editor.selectedText : "", replacing);
}
let symbolTimer = null, symbolGeneration = 0;
function symbolsChanged() {
  clearTimeout(symbolTimer);
  symbolTimer = setTimeout(refreshSymbols, 300);
}
// The program's sources, as the editor has them: the file Run starts, the
// files it Includes, then, unless withTabs is false, the other files open in
// tabs. [{ path, text }]
async function programSources(withTabs = true) {
  if (!project) return [];
  const opened = editor.texts();
  const textOf = async (path) => opened.get(path) ?? await project.text(path);
  const order = [], seen = new Set();
  const visit = async (path) => {
    const found = path && projectPath(path);
    if (!found || seen.has(found) || !/\.(bb|bbinc|inc)$/i.test(found)) return;
    seen.add(found);
    const text = await textOf(found);
    order.push({ path: found, text });
    for (const included of includes(found, text, programFolder)) await visit(included);
  };
  const programFolder = project.main && project.main.includes("/") ?
    project.main.slice(0, project.main.lastIndexOf("/") + 1) : "";
  await visit(project.main);
  if (withTabs) for (const path of tabs.paths) await visit(path);
  return order;
}
async function refreshSymbols() {
  if (!project) return;
  const generation = ++symbolGeneration;
  const opened = editor.texts();
  const textOf = async (path) => opened.get(path) ?? await project.text(path);
  const order = await programSources();
  if (generation !== symbolGeneration) return;
  symbolPane.update(order);
  const texts = new Map(order.map(({ path, text }) => [path, text]));
  const breakpoints = [];
  for (const [path, rows] of editor.breakpoints()) {
    const lines = (texts.get(path) ?? await textOf(path)).split("\n");
    breakpoints.push({ path, rows: rows.map((row) => ({ row, text: lines[row - 1] || "" })) });
  }
  if (generation !== symbolGeneration) return;
  symbolPane.setBreakpoints(breakpoints);
}

const closeTab = attempt("The tab could not be closed", async (path) => {
  if (path === MODULES) {
    if (modules) modules.closed();
    const next = tabs.close(MODULES);
    if (next && next !== HELP) await openFile(next);
    else showHelpView();
    return;
  }
  await saveAll();
  editor.close(path);
  const next = tabs.close(path);
  // The code beside the World Editor goes with its tab.
  if (path === besideToolPath) hideBesideTool();
  if (next && next !== HELP) await openFile(next);
  else showHelpView();
});
// The help, in its tab, the first, in the editor's place: as in the Windows
// IDE, it is also what shows when no file is open.
function showHelpView() {
  showPreview(null);
  tree.select(null);
  tabs.showHelp();
  $("editor").hidden = true;
  $("help-pane").hidden = false;
  $("modules-pane").hidden = true;
  if (!$("help-frame").src) help.show();
  editorCommands();
}

// The Modules page (modules.js), in its tab, in the editor's place.
function showModulesView() {
  showPreview(null);
  tree.select(null);
  tabs.showModules();
  $("editor").hidden = true;
  $("modules-pane").hidden = false;
  editorCommands();
}

function showPreview(path) {
  $("help-pane").hidden = true;
  $("modules-pane").hidden = true;
  const preview = $("preview");
  if (previewUrl) URL.revokeObjectURL(previewUrl);
  previewUrl = null;
  modelViewer.stop();
  preview.replaceChildren();
  preview.hidden = !path;
  $("editor").hidden = !!path;
  editorCommands();
  if (!path) return;
  const blob = project.blob(path);
  const heading = document.createElement("p");
  heading.textContent = `${path}, ${formatSize(blob.size)}`;
  preview.append(heading);
  if (IMAGE.test(path) || SOUND.test(path)) {
    previewUrl = URL.createObjectURL(blob);
    const media = document.createElement(IMAGE.test(path) ? "img" : "audio");
    media.src = previewUrl;
    if (media.tagName === "AUDIO") media.controls = true;
    else media.alt = path;
    preview.append(media);
  } else if (MODEL.test(path) && canRun) {
    modelViewer.show(preview, path, projectAssets);
  } else {
    const note = document.createElement("p");
    note.className = "muted";
    note.textContent = "The program can load this file. The editor does not show files of this kind.";
    preview.append(note);
  }
}

const formatSize = (bytes) => bytes < 1024 ? `${bytes} bytes` :
  bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;

// Breakpoints are kept in the browser, for each project.
function saveBreakpoints() {
  if (!project) return;
  const all = Object.fromEntries(editor.breakpoints());
  if (Object.keys(all).length) remember.set(BREAKPOINTS + project.id, JSON.stringify(all));
  else try { localStorage.removeItem(BREAKPOINTS + project.id); } catch {}
}
async function loadBreakpoints() {
  let saved = {};
  try { saved = JSON.parse(remember.get(BREAKPOINTS + project.id)) || {}; } catch {}
  for (const [path, rows] of Object.entries(saved)) {
    if (project.has(path) && TEXT.test(path) && Array.isArray(rows)) {
      editor.setBreakpoints(path, await project.text(path), rows);
    }
  }
}

// Opens a project: its main file, or else its first file.
const showProject = async (opened) => {
  await saveAll();
  liveIdle();
  if (stopRunner()) write("Stopped.", "note");
  debugPane.ended();
  toolClosed();
  layout.showProgram(false);
  setRunning(false);
  project = opened;
  if (unwatchProject) unwatchProject();
  unwatchProject = project.watch(behaviourCodeChanged);
  // Notices about the last project no longer apply.
  for (const kind of ["share", "example", "permission"]) clearNotices(kind);
  editor.closeAll();
  await loadBreakpoints();
  showPreview(null);
  consoleElement.replaceChildren();
  assetCache = null;
  $("project-name").value = project.name;
  $("folder-note").hidden = !project.isFolder;
  $("folder-note").textContent = project.isFolder ? `In the folder ${project.meta.folderName} on this computer` : "";
  remember.set(LAST_PROJECT, project.id);
  tree.show(project, null);
  // The tabs the program had open, or else its main file.
  let kept = null;
  try { kept = JSON.parse(remember.get(TABS + project.id)); } catch {}
  const keptPaths = kept && Array.isArray(kept.paths) ? kept.paths.filter((path) => project.has(path)) : [];
  tabs.reset(keptPaths, null);
  const first = (kept && keptPaths.includes(kept.active) && kept.active) || keptPaths[0] || project.main ||
    project.paths().find((path) => TEXT.test(path)) || project.paths()[0];
  if (first) await openFile(first);
  document.title = `${project.name} - Blitz3D+ Online`;
  if (account) {
    account.refresh();
    account.checkRemote(project);
  }
  if (blip) await blip.projectChanged();
  if (buddies) await buddies.projectChanged(project);
  if (modules) modules.projectChanged();
};

// Adds files to the project: [[path, File or bytes], ...].
const addFiles = attempt("The files could not be added", async (files) => {
  let added = 0, last = null;
  for (const [path, contents] of files) {
    last = await project.write(path, contents);
    ++added;
  }
  if (!project.main) await project.update({ main: guessMainFile(project.paths(), project.name) });
  tree.show(project);
  status(`Added ${added} file${added === 1 ? "" : "s"}.`);
  liveChanged();
  if (added === 1 && TEXT.test(last)) await openFile(last);
});

// A new, empty source file, named at once: the Files pane's New, File > New
// file and the toolbar's New, as the desktop IDEs' New makes one in a project.
const newFile = attempt("The file could not be made", async () => {
  closeMenu();
  let name = "untitled.bb";
  for (let n = 2; project.has(name); ++n) name = `untitled${n}.bb`;
  await project.write(name, "");
  if (!project.main) await project.update({ main: name });
  tree.show(project);
  await openFile(name);
  const row = $("files").querySelector(`[data-path="${CSS.escape(name)}"]`);
  if (row) tree.rename(row, name);
});
$("new-file").addEventListener("click", newFile);
$("menu-new-file").addEventListener("click", newFile);
$("tool-new").addEventListener("click", newFile);

// Add files: the Files pane's Add…, File > Add files… and the toolbar's Open
// copy files from this computer into the project, as the desktop IDEs' Add a
// copy does.
for (const id of ["add-files", "menu-add-files"]) {
  $(id).addEventListener("change", async (event) => {
    closeMenu();
    const files = [...event.target.files].map((file) => [file.name, file]);
    event.target.value = "";
    if (files.length) await addFiles(files);
  });
}
$("tool-open").addEventListener("click", () => $("add-files").click());

// Projects: new, opened, imported, downloaded, renamed and deleted.
const createProject = async (options) => {
  const created = await store.create(options);
  await showProject(created);
  return created;
};

const openProjectById = attempt("The program could not be opened", async (id, fromClick = false) => {
  try {
    await showProject(await store.openProject(id, fromClick));
  } catch (error) {
    if (!error.needsPermission) throw error;
    // A folder on disk needs a click to allow access again.
    notice(error.message + ".", [["Allow access", () => openProjectById(id, true)]], "permission");
    return false;
  }
  return true;
});

const newProject = attempt("The new project could not be made", async () => {
  closeMenu();
  const name = await store.uniqueName("Untitled");
  await createProject({ name, files: [[`${name.replace(/[^\w -]/g, "")}.bb`, ""]] });
  $("project-name").select();
});
$("new-project").addEventListener("click", newProject);

// Says what an import brought, and which file Run starts.
const importedStatus = (count) => {
  const sources = project.paths().filter((path) => /\.bb$/i.test(path)).length;
  status(`Imported ${count} files as ${project.name}. Run starts ${project.main || "no file yet"}` +
    (sources > 1 ? "; to run another, double-click it in the file list." : "."));
};

// A zip whose files are all in one folder is imported without that folder.
const withoutCommonFolder = (entries) => {
  const tops = new Set(entries.map(([path]) => path.includes("/") ? path.split("/")[0] : ""));
  if (tops.size !== 1 || tops.has("")) return { folder: "", entries };
  const [folder] = tops;
  return { folder, entries: entries.map(([path, contents]) => [path.slice(folder.length + 1), contents]) };
};

$("import-zip").addEventListener("change", attempt("The zip file could not be imported", async (event) => {
  closeMenu();
  const file = event.target.files[0];
  event.target.value = "";
  if (!file) return;
  const { folder, entries } = withoutCommonFolder(await readZip(await file.arrayBuffer()));
  const name = folder || file.name.replace(/\.zip$/i, "");
  const files = entries.filter(([path]) => !path.split("/").some((part) => part.startsWith(".") || part === "__MACOSX"));
  await createProject({ name, files, main: await findMainFile(files, name) });
  importedStatus(files.length);
}));

$("import-folder").addEventListener("change", attempt("The folder could not be imported", async (event) => {
  closeMenu();
  const { folder, files } = chosenFolder(event.target.files);
  event.target.value = "";
  if (!files.length) return;
  await createProject({ name: folder || "Imported", files, main: await findMainFile(files, folder) });
  importedStatus(files.length);
}));

$("open-folder").hidden = !store.canOpenFolders;
$("open-folder").addEventListener("click", attempt("The folder could not be opened", async () => {
  closeMenu();
  let handle;
  try {
    handle = await window.showDirectoryPicker({ mode: "readwrite" });
  } catch (error) {
    if (error.name === "AbortError") return;
    throw error;
  }
  await showProject(await store.addFolder(handle));
  status(`Editing the folder ${handle.name} in place. Changes are saved to it.`);
}));

$("download-project").addEventListener("click", attempt("The project could not be downloaded", async () => {
  closeMenu();
  await saveAll();
  download(await projectZip(project.paths().map((path) => [path, project.blob(path)])), `${project.name}.zip`);
}));

// A web build, as the desktop IDE's Create web build writes one: a page that
// holds the whole program, or a zip of a site's files, for very large games.
// It is never a debug or live build. Returns what export.js makes it from, or
// null after showing why the program could not be built.
const webBuild = async () => {
  closeMenu();
  status("Making a web build…");
  const built = await build(false, { exported: true });
  if (!built || built.failed) return null;
  return { runtime: new URL("run/", location.href), title: built.title, launchDirectory: built.launchDirectory,
    debug: false, wasm: built.wasm, assets: built.assets, loads: built.loads };
};

$("download-page").addEventListener("click", attempt("The web build could not be made", async () => {
  const site = await webBuild();
  if (!site) return;
  const page = await webPage(site);
  const name = `${site.title}.html`;
  download(page, name);
  status(`Made ${name} (${(page.size / 1048576).toFixed(1)} MB), which holds the whole program: open it in a ` +
    "browser, or upload it to any web host that serves HTTPS.");
}));

// The site's page downloads first what the program needs to start: the files
// it loads by name, and the worlds it loads with what they need at the start.
$("download-web").addEventListener("click", attempt("The web build could not be made", async () => {
  const site = await webBuild();
  if (!site) return;
  const listed = await listStart(site);
  if (!listed.ok) {
    write("The web build has no start list, so its page starts the program at once and gets each file as the " +
      `program opens it. ${listed.output}`, "note");
  }
  download(await webBuildZip({ ...site, start: listed.start }), `${site.title} web build.zip`);
  status(`Made a web build of ${site.title}: unzip it on any web server to publish it.`);
}));

$("delete-project").addEventListener("click", attempt("The project could not be deleted", async () => {
  closeMenu();
  const what = project.isFolder ? `Remove ${project.name} from this list? Its folder on this computer is kept.` :
    project.meta.cloud ? `Delete ${project.name} from this browser? It stays in your account.` :
    `Delete ${project.name} and all its files from this browser? This cannot be undone.`;
  if (!confirm(what)) return;
  const id = project.id;
  await store.remove(id);
  const others = await store.list();
  if (others.length && await openProjectById(others[0].id)) return;
  await openExample(firstExample);
}));

$("project-name").addEventListener("change", attempt("The project could not be renamed", async () => {
  const name = $("project-name").value.trim();
  if (!name || name === project.name) {
    $("project-name").value = project.name;
    return;
  }
  await project.update({ name });
  document.title = `${project.name} - Blitz3D+ Online`;
}));
$("debug").checked = remember.get(DEBUG) !== "0";
$("debug").addEventListener("change", () => {
  remember.set(DEBUG, $("debug").checked ? "1" : "0");
  liveChanged();
});

// The menus, File, Edit and View, and the list of projects. As in Windows' menu
// bars, pointing at another menu while one is open opens that one instead.
// The menus are made from the IDEs' shared command table
// (modern/ide/ide_commands.json, tools/ide_commands.mjs) when the site is built.
const menus = [...document.querySelectorAll("#menubar .menu-button")]
  .map((button) => [button, $(button.getAttribute("aria-controls"))]);
// Menu items that do what a toolbar button or check box does, and are
// available when it is.
const MENU_MIRRORS = [["menu-run", "run"], ["menu-stop", "stop"], ["menu-fullscreen", "fullscreen"],
  ["menu-continue", "debug-continue"], ["menu-step-over", "debug-step-over"], ["menu-step-into", "debug-step-into"],
  ["menu-step-out", "debug-step-out"], ["menu-world", "tool-world"], ["menu-help-home", "help-home"],
  ["menu-help-back", "help-back"], ["menu-help-forward", "help-forward"]];
const MENU_CHECKS = [["menu-debug", "debug"], ["menu-live", "live"]];
function syncMenus() {
  for (const [item, control] of MENU_MIRRORS) {
    // The debugger's buttons are there only while a debug run is stopped.
    const off = $(control).disabled || (control.startsWith("debug-") && !document.body.classList.contains("debugging"));
    $(item).disabled = off;
  }
  $("menu-run-stopped").disabled = $("run").disabled;
  $("menu-run-online").disabled = $("run").disabled;
  for (const [item, box] of MENU_CHECKS) $(item).setAttribute("aria-checked", String($(box).checked));
}
const closeMenu = () => {
  for (const [button, menu] of menus) {
    menu.hidden = true;
    button.setAttribute("aria-expanded", "false");
  }
};
for (const [button, menu] of menus) {
  const open = () => {
    closeMenu();
    syncMenus();
    menu.hidden = false;
    button.setAttribute("aria-expanded", "true");
  };
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    if (!menu.hidden) return closeMenu();
    open();
    menu.querySelector("button:not([hidden]), label").focus();
  });
  button.addEventListener("pointerenter", () => {
    if (menu.hidden && menus.some(([, other]) => !other.hidden)) open();
  });
}
addEventListener("pointerdown", (event) => {
  if (!menus.some(([button, menu]) => menu.contains(event.target) || button.contains(event.target))) closeMenu();
});
addEventListener("keydown", (event) => { if (event.key === "Escape") closeMenu(); });
// The Project menu's file choices are labels, which the keyboard reaches too.
for (const label of $("project-menu").querySelectorAll("label")) {
  label.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    label.querySelector("input").click();
  });
}

const showProjects = async () => {
  closeMenu();
  const list = $("project-list");
  list.replaceChildren();
  for (const meta of await store.list()) {
    const row = document.createElement("li");
    const open = document.createElement("button");
    open.type = "button";
    open.className = "open";
    const name = document.createElement("strong");
    name.textContent = meta.name;
    const detail = document.createElement("span");
    // A buddy session's copy of someone else's program says whose.
    const buddies = meta.buddies && !meta.buddies.host ?
      `${meta.buddies.hostName ? `${meta.buddies.hostName}'s buddy session` : "Buddy session"}` +
      `${meta.buddies.ended ? ", ended" : ""} · ` : "";
    detail.textContent = (meta.kind === "folder" ? `Folder ${meta.folderName} · ` : "") + buddies +
      `changed ${new Date(meta.updated).toLocaleString()}`;
    open.append(name, detail);
    open.addEventListener("click", async () => {
      $("projects-dialog").close();
      await openProjectById(meta.id, true);
    });
    if (project && meta.id === project.id) row.className = "current";
    row.append(open);
    list.append(row);
  }
  // The account's projects that are not in this browser yet.
  const remoteList = $("remote-project-list");
  remoteList.replaceChildren();
  const remote = account ? await account.remoteOnly(await store.list()) : [];
  $("remote-projects").hidden = !remote.length;
  for (const item of remote) {
    const row = document.createElement("li");
    const open = document.createElement("button");
    open.type = "button";
    open.className = "open";
    const name = document.createElement("strong");
    name.textContent = item.name;
    const detail = document.createElement("span");
    detail.textContent = `${item.files} files · saved ${new Date(item.updated).toLocaleString()}`;
    open.append(name, detail);
    open.addEventListener("click", attempt("The program could not be opened", async () => {
      $("projects-dialog").close();
      await account.openRemote(item.id);
    }));
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "remove";
    remove.textContent = "Delete";
    remove.title = `Delete ${item.name} from your account`;
    remove.addEventListener("click", attempt("The files could not be deleted", async () => {
      if (!confirm(`Delete ${item.name} from your account? This cannot be undone.`)) return;
      await account.removeRemote(item.id);
      row.remove();
    }));
    row.append(open, remove);
    remoteList.append(row);
  }
  $("projects-dialog").showModal();
};
$("open-project").addEventListener("click", showProjects);
$("close-projects").addEventListener("click", () => $("projects-dialog").close());

// Examples: the help's command examples and the samples, which the help's
// links open, each as a project with the media it loads. Opening one again
// opens the same project, with any changes.

// The file a project made from an example runs. A command example's path is
// from the help's command folder, and a GUI sample's names its own base; a
// sample folder names its main program, and all its files are from its base.
const exampleMain = (example) => example.main || example.path.split("/").pop();
const exampleFolder = (example) => example.main ? example.base :
  (example.base || "help/commands/") + example.path.slice(0, example.path.lastIndexOf("/") + 1);
const exampleFiles = async (example) => {
  const folder = exampleFolder(example);
  const fetchFile = async (path) => {
    const response = await fetch(versioned(folder + path.split("/").map(encodeURIComponent).join("/")));
    if (!response.ok) throw new Error(`${path} is missing (${response.status})`);
    return [path, new Uint8Array(await response.arrayBuffer())];
  };
  return Promise.all((example.main ? example.files : [exampleMain(example), ...example.files]).map(fetchFile));
};

// The example a program on the site belongs to, by the program's path from
// the IDE's folder, as help/commands/2d_examples/Plot.bb or
// samples/shaders/showcase/45_fur.bb, and the program's path in the project.
const exampleAt = (site) => {
  const wanted = site.toLowerCase();
  for (const example of examples) {
    const folder = exampleFolder(example);
    const programs = example.programs ? Object.keys(example.programs) : [exampleMain(example)];
    const program = programs.find((name) => (folder + name).toLowerCase() === wanted);
    if (program) return { example, program };
  }
  return null;
};

// Opens an example as a project. program, a link's choice of one of a sample
// folder's programs, becomes the one Run starts, and shows.
const openExample = attempt("The example could not be opened", async (example, program) => {
  let existing = (await store.list()).find((meta) => meta.example === example.path);
  // An example opened before is the user's copy. One they never changed
  // follows the site: after a publish, it is copied again from the new files.
  const older = existing && SITE_VERSION && existing.siteVersion !== SITE_VERSION;
  if (existing && older && !existing.modified) {
    await store.remove(existing.id);
    existing = null;
  }
  if (existing && await openProjectById(existing.id)) {
    if (project.meta.modified) {
      notice(older ? `This site has a newer version of ${example.name}. Your copy keeps your changes.` :
        `You have changed ${example.name} before.`,
      [[older ? "Start over from the new version" : "Start over from the help's version", async () => {
        await store.remove(project.id);
        await openExample(example, program);
      }]], "example");
    }
  } else {
    await createProject({ name: example.name, files: await exampleFiles(example), main: exampleMain(example),
      example: example.path, mode: example.mode, programs: example.programs, shaders: example.shaders,
      siteVersion: SITE_VERSION });
  }
  if (program && project && program !== project.main && project.has(program)) {
    await tree.actions.setMain(program);
    await openFile(program);
  }
  // A sample folder with several programs says how to run the others.
  const others = example.programs ? Object.keys(example.programs).length - 1 : 0;
  if (others > 0) {
    notice(`${example.name} has ${others + 1} programs. Run starts ${project.main}; to run another, double-click ` +
      "it in the file list.", [], "example");
  }
  if (example.outside) {
    notice(`${example.name} also loads files from outside the help (${example.outside.join(", ")}), ` +
      "which the online IDE does not have.", [], "warning");
  }
});
// The help, beside the program.
const help = new Help({
  frame: $("help-frame"),
  base: "help/",
  onExample: (path) => {
    const found = exampleAt(path);
    if (found) openExample(found.example, found.program);
    else notice(`The example ${path.split("/").pop()} is not in the online IDE.`, [], "warning");
  },
  onNavigate: () => {
    $("help-back").disabled = !help.canGoBack;
    $("help-forward").disabled = !help.canGoForward;
  },
});
await help.load();
// F1, as the Windows IDE has it: on a function of the program's, goes to
// its definition; otherwise the first press shows the command's syntax in
// the status bar, and a second, on the same word, opens its help page.
let lastQuickHelp = "";
// Extended's language words, documented in the language reference rather
// than on command pages.
const LANGUAGE_PAGES = {
  enum: "enums", interface: "interfaces", implements: "interfaces",
  list: "collections", map: "collections", queue: "collections", stack: "collections", set: "collections",
  as: "collections", module: "modules", import: "modules", private: "modules",
  method: "typemethods", self: "typemethods", worker: "workers",
};
const WIDE_NUMBER_COMMANDS = new Set(("long double floatbits floatfrombits doublebits doublefrombits peeklong " +
  "pokelong peekdouble pokedouble readlong writelong readdouble writedouble sind cosd tand asind acosd atand " +
  "atan2d sqrtd expd logd log10d floord ceild truncd roundd powd modd sinradd cosradd tanradd asinradd " +
  "acosradd atanradd atan2radd").split(" "));
// A command's syntax, as blitzcc +k writes it for the desktop IDEs (quickHelp
// in blitz/main.cpp): "CreateCube ( [parent] )", "Rnd# ( from#[,to#] )", with
// the comma of an optional parameter after the first inside its brackets,
// "Cls ", and a built-in constant's value: "JOB_SUCCEEDED=3".
const SYNTAX_TYPES = { "#": "#", "$": "$", "&": ":Long", "@": ":Double" };
function syntaxOf(word) {
  const command = commands.get(word.toLowerCase());
  const constant = constantOf(word);
  if (constant) return `${constant.name}=${constant.value}`;
  if (!command) return isKeyword(word) ? word : "";
  const params = command.params.map((param, k) => {
    const text = (k ? "," : "") + param.name + (SYNTAX_TYPES[param.sigil] ?? "");
    return param.default === undefined ? text : `[${text}]`;
  }).join("");
  const name = command.name + (SYNTAX_TYPES[command.returns] ?? "") + " ";
  return command.returns ? `${name}( ${params}${command.params.length ? " )" : ")"}` : name + params;
}
async function showHelpFor(word) {
  if (!word) {
    status("Put the cursor on a command, then press F1 for its help.");
    return;
  }
  const key = word.toLowerCase();
  if (!isKeyword(word) && !commands.has(key)) {
    for (const { path, text } of await programSources()) {
      const found = index(text).functions.find((symbol) => symbol.name.toLowerCase() === key);
      if (found) {
        await showLocation(path, found.line + 1, 1);
        status(`Function ${found.name}, in ${path}.`);
        return;
      }
    }
  }
  const page = WIDE_NUMBER_COMMANDS.has(key) ? help.pageAt("language/lang_ref_widenumbers.html")
    : LANGUAGE_PAGES[key] ? help.pageAt(`language/lang_ref_${LANGUAGE_PAGES[key]}.html`)
    : help.pageFor(word);
  const syntax = syntaxOf(word);
  if (key !== lastQuickHelp) {
    lastQuickHelp = key;
    if (syntax) {
      status(page ? `${syntax}    (F1 again for its help page)` : syntax);
      return;
    }
  }
  lastQuickHelp = "";
  if (!page) {
    status(`There is no help page for ${word}.`);
    return;
  }
  help.show(page);
  showHelp();
  status(`Help for ${word}.`);
}
// Shows the help, in its tab, the first, in the editor's place.
function showHelp() {
  showHelpView();
}
// The toolbar's Home, Back and Forward, as the desktop IDE's, move through
// the help.
$("help-home").addEventListener("click", () => {
  help.show();
  showHelp();
});
$("help-back").addEventListener("click", () => {
  help.back();
  showHelp();
});
$("help-forward").addEventListener("click", () => {
  help.forward();
  showHelp();
});

// Share links hold one source file.
$("share").addEventListener("click", attempt("The link could not be made", async () => {
  await saveAll();
  const paths = project.paths();
  let url;
  if (paths.length === 1 && /\.bb$/i.test(paths[0])) {
    // A one-file program travels in the link itself.
    const fragment = await encodeShare({ title: project.name, mode: "extended", source: await project.text(paths[0]) });
    url = `${location.origin}${location.pathname}#${fragment}`;
  } else {
    // Other projects are shared from the account (account.js).
    status("Making a share link…");
    const result = account ? await account.shareLink(project) :
      { error: "A link can hold a program of one source file. Download its files to share them." };
    if (result.error) {
      clearNotices("share");
      notice(result.error, [], "share");
      return;
    }
    url = result.url;
    status(`Made a share link to a copy of ${project.name} as it is now.`);
  }
  let copied = false;
  try {
    await navigator.clipboard.writeText(url);
    copied = true;
  } catch {}
  const long = url.length > 8000 ? " It is long, so some sites may cut it short." : "";
  clearNotices("share");
  const element = notice(copied ? `Link copied.${long}` : `Copy this link to share the program.${long}`, [], "share");
  const field = document.createElement("input");
  field.className = "share-link";
  field.readOnly = true;
  field.value = url;
  field.setAttribute("aria-label", "Share link");
  field.addEventListener("focus", () => field.select());
  element.insertBefore(field, element.querySelector(".dismiss"));
  state.shareLink = url;
}));

// Opens the shared program in the URL's fragment, if it holds one, as a new
// project, in the full-width editor.
const openShared = attempt("The shared program could not be opened", async () => {
  if (location.hash.length <= 1) return false;
  // An invite to a buddy session: #join=<room>.<token>.
  if (buddies && /(^#|&)join=/.test(location.hash)) return buddies.openInvite();
  const token = new URLSearchParams(location.hash.slice(1)).get("s");
  if (token) {
    history.replaceState(null, "", location.pathname + location.search);
    if (!account) {
      notice("This share link needs the online IDE's server, which this site does not have.", [], "warning");
      return false;
    }
    await account.openShare(token);
    return true;
  }
  let shared = null;
  try {
    shared = await decodeShare(location.hash);
  } catch {
    layout.restore();
    notice("The shared program in this link could not be read.", [], "warning");
  }
  // Reloading then keeps the edits, rather than opening the link again.
  history.replaceState(null, "", location.pathname + location.search);
  if (!shared) return false;
  const file = `${shared.title.replace(/[\\/:*?"<>|]/g, "") || "Shared"}.bb`;
  await createProject({ name: shared.title, files: [[file, shared.source]], mode: shared.mode });
  layout.widen();
  status(`Opened the shared program ${shared.title}: a copy that is yours to change.`);
  return true;
});
// A link pasted into this tab changes only the fragment.
addEventListener("hashchange", () => openShared());

// Running.
const runner = new Runner({
  pane: $("run-pane"),
  runPath: new URL("run/index.html", location.href).pathname,
  runOriginFor: (n) => {
    if (params.get("run")) return params.get("run").replace("{n}", String(n));
    // A server names its run frame's origin.
    if (cloud && cloud.config.runOrigin) return cloud.config.runOrigin.replace("{n}", String(n));
    const url = new URL(location.href);
    // On localhost, where passkeys work, the run frames are on r0.localhost,
    // r1.localhost, ..., each a site of its own; on 127.0.0.1, on localhost
    // and then r1.localhost, ...
    if (url.hostname === "localhost") url.hostname = `r${n}.localhost`;
    else if (url.hostname === "127.0.0.1") url.hostname = n ? `r${n}.localhost` : "localhost";
    // A static host with no server, such as GitHub Pages, has one site: the
    // run frames share it, and a program that never yields may need the page
    // reloaded after Stop.
    return url.origin;
  },
  events: {
    started() {
      const record = state.lastRun;
      if (record.check) {
        status("Blip is trying your program…");
        return;
      }
      if (record.online) {
        status("Online test: you have joined the server in this browser. End, or the program's own way out, ends the test.");
        return;
      }
      status(!record.live ? `Running. Compiled in ${Math.round(record.compileMs)} ms.` :
        record.reason ? `Restarted: ${record.reason}` :
        record.restarted ? `Restarted with your changes. Compiled in ${Math.round(record.compileMs)} ms.` :
        "Running. Live restarts it with your changes as you type.");
    },
    output(lines) {
      state.lastRun.firstOutputMs ??= performance.now() - state.lastRun.started;
      // Blip's test run reads what the program prints, but not the runtime's
      // note of the frame it captured for him.
      const check = state.lastRun.check;
      if (check) lines = lines.filter((line) => !/^Captured frame \d+ to /.test(line.text));
      if (check) check.output.push(...lines.map((line) => line.text));
      for (const line of lines) {
        // The runtime also reports its error when the program ends, below.
        if (line.error && line.text.startsWith("Runtime Error: ")) continue;
        write(line.text, line.error ? "error" : "");
      }
    },
    firstFrame() {
      // A program that draws without saying how it shows (a GUI program)
      // shows in the pane.
      if (runner.window.state !== "window") layout.showProgram(true);
      const record = state.lastRun;
      record.firstFrameMs = performance.now() - record.started;
      if (!record.live && !record.check && !record.online) status(`Running. Compiled in ${Math.round(record.compileMs)} ms; drawing ${Math.round(record.firstFrameMs)} ms after Run.`);
    },
    // The close button of a program's window.
    close: () => stop(),
    // Where the program shows: in a window of its own, the pane is the
    // functions'; in the pane, it is the program's. Stopping a program takes
    // its window away first: only a program still running takes the pane.
    float: (floating) => {
      document.documentElement.classList.toggle("program-floating", floating);
      showDocked();
      if (floating) layout.showProgram(false);
      else queueMicrotask(() => layout.showProgram(runner.running));
    },
    // A file the World Editor saved in the project, or deleted.
    file: (path, bytes) => { toolFiles = toolFiles.then(() => keepToolFile(path, bytes)); },
    // The program's messages to the page (SendPageMessage): the World Editor's
    // Play asks for a program to be run on its world, it says which world it
    // has open, and a shared world's go to its buddy session.
    pageMessage: (message, file) => {
      if (message && message.protocol === "blitz3d.editor-host" && message.type === "play" &&
          state.lastRun && state.lastRun.tool) {
        playFromWorldEditor(message, file);
        return;
      }
      // An interior's build, beside the editor (buildInteriorForWorldEditor).
      if (message && message.protocol === "blitz3d.editor-host" && message.type === "build-interior" &&
          state.lastRun && state.lastRun.tool) {
        buildInteriorForWorldEditor(message);
        return;
      }
      // A recipe's Make (blip_recipes.bb): asked of Blip, as a message in his
      // conversation, and the editor told whether it was.
      if (message && message.protocol === "blitz3d.editor-host" && message.type === "make-recipe" &&
          state.lastRun && state.lastRun.tool) {
        makeRecipeForWorldEditor(message);
        return;
      }
      // Open code: a behaviour's code, beside the editor.
      if (message && message.protocol === "blitz3d.editor-host" && message.type === "open" &&
          state.lastRun && state.lastRun.tool) {
        openBesideTool(appPath(message.file));
        return;
      }
      if (message && message.protocol === SESSION_PROTOCOL && message.type === "opened") worldOpened(message);
      if (sharedWorld) sharedWorld.receive(message, file);
    },
    debug: (event) => {
      // Automated checks read a debug run's events.
      state.lastRun.debugEvents.push(event);
      debugPane.event(event);
    },
    ended(code, message, location, capture) {
      debugPane.ended();
      layout.showProgram(false);
      const record = state.lastRun;
      // An online test ends with its player.
      if (onlineTest && onlineTest.record === record) endOnlineTest();
      if (record.check) {
        // Blip's test run: its error goes to the console, and to Blip, who
        // fixes it; the editor and the cursor stay as they are.
        record.endedMs = performance.now() - record.started;
        record.ended = { status: code, message, location };
        setRunning(false);
        if (code !== 0 && message) write(message, "error");
        write(code === 0 ? "Blip's test run ended." : `Blip's test run ended (${code}).`, "note");
        status(code !== 0 ? "Blip's test run stopped with an error." : "Blip's test run ended.");
        if (record.check.resolve) {
          record.check.resolve({ status: code !== 0 ? "error" : capture ? "captured" : "ended", message, location,
            capture });
        }
        return;
      }
      if (record.tool) {
        record.endedMs = performance.now() - record.started;
        record.ended = { status: code, message, location };
        setRunning(false);
        toolClosed();
        if (code !== 0 && message) write(message, "error");
        const tool = record.tool === "asset-library" ? "The asset library" : "The World Editor";
        status(code === 0 ? `${tool} closed.` : `${tool} stopped with an error.`);
        return;
      }
      record.endedMs = performance.now() - record.started;
      record.ended = { status: code, message, location };
      setRunning(false);
      // A program the World Editor played gives the world back to the editor,
      // unless it stopped with an error to look at.
      if (record.play && code === 0) {
        write("Program ended.", "note");
        returnToWorldEditor(record);
        return;
      }
      if (code !== 0 && message && record.errorShown) {
        // The debugger showed the error when the program stopped at it.
        status("The program ended at its error.");
      } else if (code !== 0 && message) {
        showRuntimeError(message, location);
        status($("debug").checked || location ? "The program stopped with an error." :
          "The program stopped with an error. Turn on Debug to see where.");
        if (blip) blip.offer({ kind: "runtime", message, location });
      } else {
        status("The program ended.");
      }
      write(code === 0 ? "Program ended." : `Program ended (${code}).`, "note");
    },
    responding(responding) {
      state.notResponding = !responding;
      // A test run that stops answering is ended, after a moment.
      const check = state.lastRun && state.lastRun.check;
      if (check && check.resolve && !responding) {
        setTimeout(() => {
          if (state.notResponding && check.resolve) check.resolve({ status: "not-responding" });
        }, 4000);
      }
      if (!responding) status("The program is not responding. Stop ends it.");
      else if (runner.running) status("Running.");
    },
  },
});
// The preview of a 3D model (modelviewer.js), in run frames of its own.
const modelViewer = new ModelViewer({ base: new URL("tools/model-viewer/", location.href),
  runPath: runner.runPath, runOriginFor: runner.runOriginFor });

// Shows a runtime error in the console, and marks it on its line when the
// program says where: the file, by its path in the project.
function showRuntimeError(message, location) {
  const text = location ? message.replace(/\s*\([^()]* line \d+\)$/, "") : message;
  if (location && projectPath(location.file)) {
    writeLocation(projectPath(location.file), location.line, 0, text);
    markErrors([{ file: location.file, row: location.line, col: 1, message: `Runtime error: ${text}` }]);
  } else {
    write(message, "error");
  }
}

// The debugger, for debug runs.
// The tabs under the editor: the console, and the debugger while a debug
// run is stopped or has stopped, as the original IDE's debugger had them.
const bottomTabs = {
  select(tab) {
    const debug = tab === "debug" && !$("tab-debug").hidden;
    const memory = tab === "memory";
    $("tab-console").setAttribute("aria-selected", String(!debug && !memory));
    $("tab-debug").setAttribute("aria-selected", String(debug));
    $("tab-memory").setAttribute("aria-selected", String(memory));
    $("console").hidden = debug || memory;
    $("debugger").hidden = !debug;
    $("debug-memory").hidden = !memory;
    $("clear-console").hidden = debug || memory;
  },
  // A debug run stopped: its tab shows, and is chosen. When the run ends, it
  // goes, and the console shows again.
  debugger(visible) {
    $("tab-debug").hidden = !visible;
    this.select(!$("debug-memory").hidden ? "memory" : visible ? "debug" : "console");
  },
};
$("tab-console").addEventListener("click", () => bottomTabs.select("console"));
$("tab-debug").addEventListener("click", () => bottomTabs.select("debug"));
$("tab-memory").addEventListener("click", () => bottomTabs.select("memory"));
for (const tab of [$("tab-console"), $("tab-debug"), $("tab-memory")]) {
  tab.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const visible = ["console", "debug", "memory"].filter((id) => !$(`tab-${id}`).hidden);
    const current = visible.indexOf(tab.id.slice(4));
    const other = visible[(current + (event.key === "ArrowRight" ? 1 : visible.length - 1)) % visible.length];
    bottomTabs.select(other);
    $(`tab-${other}`).focus();
  });
}
const debugPane = setupDebugger({ $, editor, runner, projectPath, write, status,
  show: (visible) => bottomTabs.debugger(visible),
  // A debug run stopped at a runtime error.
  onError: (stopped) => {
    state.lastRun.errorShown = true;
    showRuntimeError(stopped.message, { file: stopped.file, line: stopped.line });
  },
  openAt: async (path, row) => {
    await openFile(path);
    editor.goTo(row);
  },
});

// The World Editor.
const worldEditor = new WorldEditor(new URL("tools/world-editor/", location.href));
let toolFiles = Promise.resolve();

// Runs the World Editor on a world in the project, or on a new world when
// path is null. It takes the program's place, with room to work. Its Play
// starts with program, by its path in the editor, or else the Run file.
const openWorldEditor = attempt("The World Editor could not open", async (path = null, program = null) => {
  if (!project || !state.ready) return;
  if (!canRun) {
    status("This browser cannot run the World Editor.");
    return;
  }
  if (programRunning) {
    status(WORLD_WAITS);
    return;
  }
  await saveAll();
  liveIdle();
  if (stopRunner()) write("Stopped.", "note");
  // A World Editor stopped just now shares its world no more: its relay would
  // pass this run's edits on as someone else's.
  if (sharedWorld) {
    sharedWorld.close();
    sharedWorld = null;
  }
  debugPane.ended();
  clearNotices("world");
  state.lastRun = { started: performance.now(), compileMs: 0, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], tool: "world-editor", world: path, saved: [], projectId: project.id };
  ++state.runs;
  status(path ? `Opening ${path} in the World Editor…` : "Opening the World Editor…");
  // A program named here is the one Test played last, which it keeps;
  // otherwise the Run file is offered as Your Program.
  const chosen = program !== null;
  if (program === null) program = project.main && project.has(project.main) ? `/app/${project.main}` : "";
  const run = await worldEditor.run(path, program, chosen);
  document.body.classList.add("tool-running");
  layout.showProgram(true);
  editorCommands();
  if (blip) blip.worldEditorChanged();
  runner.run({ ...run, debug: false, assets: await toolAssets(), storageKey: "ide|world-editor" });
  setRunning(true);
  // The editor says which world it has open, now and as that changes
  // (worldOpened), and in a buddy session it is shared when the session has it.
  runner.pageMessage({ protocol: SESSION_PROTOCOL, version: 1, type: "report" });
  syncSharedWorld();
  sendLibraryAccess(state.lastRun);
  // The editor is closed as a program is ended, so the button says so.
  $("stop").textContent = "Close editor";
  $("stop").title = "Close the World Editor";
  $("stop").classList.add("labelled");
  status("The World Editor saves worlds with this program's files. Close editor ends it; save your changes first.");
});

// Whether the World Editor, rather than the asset library, is open.
function worldEditorOpen() {
  return document.body.classList.contains("tool-running") && !!state.lastRun && state.lastRun.tool === "world-editor";
}

// Behaviours of a world's own code (docs/WEB_CODE_BEHAVIOURS_DESIGN.md): a
// .bb file in a folder named behaviours, beside the world. The World Editor
// lists them and reads them, so its run keeps them, which every program's
// files leave out; and while it is open, it is given each one written,
// changed or removed by the code editor, Blip or a buddy. Its own saves it
// has already.
const BEHAVIOUR_CODE = /(^|\/)behaviours\/[^/]+\.bb$/i;
const toolAssets = () => packAssets(project.paths().map((path) => [path, project.blob(path)]), project.main,
  (path) => BEHAVIOUR_CODE.test(path));
const toolSaving = new Set();
function behaviourCodeChanged(path, blob) {
  const record = state.lastRun;
  if (!BEHAVIOUR_CODE.test(path) || toolSaving.has(path) || !worldEditorOpen() || !project ||
      !record || record.projectId !== project.id) return;
  if (!blob) {
    runner.removeFile(path);
    return;
  }
  blob.arrayBuffer().then((buffer) => {
    if (worldEditorOpen() && state.lastRun === record) runner.putFile(path, new Uint8Array(buffer));
  });
}
let unwatchProject = null;

// Open code: a file shown beside the World Editor, which keeps running in a
// narrower pane, until Back to the World Editor or the notice's ×.
let besideToolPath = null;
function showBesideTool(path) {
  besideToolPath = path;
  document.body.classList.add("tool-code");
  clearNotices("tool-code");
  const element = notice(`${path} is beside the World Editor, which keeps running.`,
    [["Back to the World Editor", hideBesideTool]], "tool-code");
  element.querySelector(".dismiss").addEventListener("click", hideBesideTool);
}
function hideBesideTool() {
  besideToolPath = null;
  document.body.classList.remove("tool-code");
  clearNotices("tool-code");
}
const openBesideTool = attempt("The file could not be opened", async (path) => {
  if (!project || !path || !project.has(path)) {
    status(`The World Editor asked for a file that is not in this program's files${path ? `: ${path}` : "."}`);
    return;
  }
  if (worldEditorOpen()) showBesideTool(path);
  await openFile(path);
});

// The shared asset library's address and a token, for the World Editor's
// Shared page and the asset library, once the token comes (library.js): none
// when the member is signed out, and the page asks them to sign in.
function sendLibraryAccess(record) {
  if (!library.available()) return;
  library.editorAccess().then((access) => {
    if (access.token && state.lastRun === record && !record.ended) {
      runner.pageMessage({ protocol: "blitz3d.library", version: 1, ...access });
    }
  });
}

// View > Asset library: the World Editor's Shared page on its own (library
// mode), where the World Editor runs. What it adds goes in the project, under
// models/ and pictures/, kept as the World Editor's saves are.
const openAssetLibrary = attempt("The asset library could not open", async () => {
  if (!project || !state.ready) return;
  if (!canRun) {
    status("This browser cannot run the asset library.");
    return;
  }
  if (!cloud || !cloud.user) {
    notice("The shared asset library needs your account: sign in to use it.", [["Sign in", () => $("account-button").click()]]);
    return;
  }
  if (programRunning) {
    status("The asset library opens once the program ends.");
    return;
  }
  await saveAll();
  liveIdle();
  if (stopRunner()) write("Stopped.", "note");
  if (sharedWorld) {
    sharedWorld.close();
    sharedWorld = null;
  }
  debugPane.ended();
  clearNotices("world");
  state.lastRun = { started: performance.now(), compileMs: 0, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], tool: "asset-library", world: null, saved: [], projectId: project.id };
  ++state.runs;
  status("Opening the asset library…");
  const run = await worldEditor.library();
  document.body.classList.add("tool-running");
  layout.showProgram(true);
  editorCommands();
  runner.run({ ...run, debug: false, assets: await projectAssets(), storageKey: "ide|world-editor" });
  setRunning(true);
  sendLibraryAccess(state.lastRun);
  $("stop").textContent = "Close library";
  $("stop").title = "Close the asset library";
  $("stop").classList.add("labelled");
  status("Add to project saves the models and pictures you choose in this program's files, under models and pictures.");
});

// Keeps a file the World Editor wrote, or removes one it deleted.
async function keepToolFile(path, bytes) {
  const record = state.lastRun;
  const name = normalisePath(path);
  // Only into the project the editor opened in.
  if (!record || !record.tool || !project || record.projectId !== project.id || !name || temporaryFile(name)) return;
  // The editor has its own saves: they are not given back to it.
  toolSaving.add(name);
  try {
    if (bytes) {
      // The shared world's buddy session hears of the save first, and the file
      // goes to it whole (buddies/world.js, buddies/session.js).
      if (sharedWorld && /\.world$/i.test(name)) await sharedWorld.saving(name, new TextDecoder().decode(bytes));
      await project.write(name, bytes, { whole: true });
      record.saved.push(name);
      write(`The World Editor saved ${name}.`, "note");
      // A world the editor saved under a new name may be one to share now.
      if (sharedWorld) sharedWorld.recheck();
    } else if (project.has(name)) {
      await project.remove(name);
      write(`The World Editor deleted ${name}.`, "note");
    } else {
      return;
    }
  } finally {
    toolSaving.delete(name);
  }
  // The text editor shows the saved version.
  const shown = editor.path === name;
  editor.close(name);
  if (!project.has(name)) tabs.remove(name);
  if (shown && project.has(name)) await openFile(name);
  tree.show(project, tree.selected);
}

// The World Editor's Play (_release/samples/world-editor/editor_play.bb): the
// editor has saved its world, and asks for a program to be run on it, by
// paths as the editor sees them. The program is one of the project's, or one
// of the players the editor carries (FPS, Fly, Orbit, Fixed, Player), whose source
// comes with the request. It runs in the editor's place, with the world's
// path as its command line, and the editor opens again on the world when it
// ends.
const appPath = (path) => typeof path === "string" ? projectPath(path.replace(/\\/g, "/").replace(/^\/app\//, "")) : null;
// A player the site keeps, in .world-editor beside the project's files, with
// the files it includes from beside it (play_common.bb, play_link.bb).
async function addPlayer(files, fileName) {
  const pending = [fileName], added = new Set();
  while (pending.length) {
    const name = pending.shift();
    if (added.has(name.toLowerCase())) continue;
    added.add(name.toLowerCase());
    const text = await worldEditor.player(name);
    files[`.world-editor/${name}`] = text;
    for (const [, included] of text.matchAll(/^[ \t]*Include[ \t]+"([^"/\\]+\.bb)"/gim)) pending.push(included);
  }
}

const playFromWorldEditor = attempt("The World Editor's program could not be played", async (request, file) => {
  if (!project || !state.ready) return;
  // The world the editor saved just before it asked.
  await toolFiles;
  const world = appPath(request.world);
  if (!world) {
    status("Play needs the world saved in this program's files.");
    return;
  }
  const program = typeof request.program === "string" ? request.program.replace(/\\/g, "/") : "";
  const name = typeof request.name === "string" && request.name ? request.name : program.slice(program.lastIndexOf("/") + 1);
  // The world's own behaviours' runner, which the editor has just written
  // beside the world (docs/WEB_CODE_BEHAVIOURS_DESIGN.md).
  const behaviours = typeof request.behaviours === "string" && request.behaviours ? appPath(request.behaviours) : null;
  if (request.behaviours && (!behaviours || !project.has(behaviours))) {
    status(`Play needs the world's behaviours.bb in this program's files: ${request.behaviours}`);
    return;
  }
  const { files } = await sources();
  let main = appPath(program), note = null;
  if (!main) {
    // A player the site keeps, or a file the editor sent; compiled beside
    // the project's files.
    const fileName = program.slice(program.lastIndexOf("/") + 1);
    const player = /\/samples\/world-editor\/play\/[^/]+\.bb$/i.test(program);
    if (!player && !(file && file.data)) {
      status(`The program to play is missing: ${program}`);
      return;
    }
    main = `.world-editor/${fileName}`;
    if (player) await addPlayer(files, fileName);
    else files[main] = new TextDecoder().decode(file.data);
    // A player runs the world's own behaviours through a program beside it.
    if (player && behaviours) {
      main = ".world-editor/play-behaviours.bb";
      files[main] = playWithBehaviours(fileName, behaviours);
    }
  } else if (behaviours && !/\bUpdateBehaviours\b/i.test(files[main] || "")) {
    // As the desktop editor says when it plays a program of the user's.
    note = `${main} does not run the world's own behaviours: include ${behaviours} and call UpdateBehaviours after UpdateWorld.`;
  }
  const debug = !!$("debug").checked && !request.extended;
  // Compiled while the World Editor still runs, so a mistake leaves it open,
  // with the code beside it and the line marked.
  const editorRecord = state.lastRun;
  const started = performance.now();
  status(`Compiling ${name}…`);
  const result = await compile(files, main, "extended", debug);
  const compileMs = performance.now() - started;
  if (state.lastRun !== editorRecord) return;
  if (!result.ok) {
    editorRecord.playError = { errors: result.errors, output: result.output };
    for (const error of result.errors) writeLocation(error.file, error.row, error.col, error.message);
    if (!result.errors.length) write(result.output, "error");
    if (note) write(note, "note");
    const shown = result.errors.find((error) => projectPath(error.file));
    if (shown && worldEditorOpen()) showBesideTool(projectPath(shown.file));
    await markErrors(result.errors);
    status(shown ? `${projectPath(shown.file)} has an error, so ${name} could not play ${world}. Fix it, then press Play again.` :
      `${name} has an error, so it could not play ${world}.`);
    return;
  }
  if (!canRun) {
    status("The program compiles, but this browser cannot run it.");
    editorRecord.playError = { status: "cannot-run" };
    return;
  }
  // The editor's place goes to the program.
  liveIdle();
  if (stopRunner()) debugPane.ended();
  toolClosed();
  setRunning(false);
  // Play Online Test: the world's own bots, and no bad network, unless Blip
  // chose others (ide.onlineTest).
  const online = request.online === true ? { bots: Number.isInteger(request.bots) ? request.bots : -1,
    network: typeof request.network === "string" ? request.network : "none", joined: false } : null;
  // Ending it goes back to the World Editor, when it played from there.
  const back = !!(editorRecord && editorRecord.tool);
  const record = state.lastRun = { started, compileMs, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], play: back ? { world, program } : null, online };
  ++state.runs;
  consoleElement.replaceChildren();
  consoleView.conceal();
  if (note) write(note, "note");
  const slash = main.lastIndexOf("/");
  const run = { title: name, debug, wasm: result.wasm, assets: await projectAssets(),
    launchDirectory: project.has(main) && slash >= 0 ? main.slice(0, slash) : ".", commandLine: `"/app/${world}"` };
  if (online) {
    startOnlineTest({ ...run, bots: online.bots, network: online.network, record });
    status(`Starting the online test of ${world} with ${name}: a server room in this browser, which you join once ` +
      `it is open.${back ? " End, or the program's own way out, goes back to the World Editor." : ""}`);
    return;
  }
  if (runner.window.docked) layout.showProgram(true);
  if (debug) debugPane.start({});
  else debugPane.newRun();
  runner.run({ ...run, storageKey: `ide|${project.id}`, attach: debug, focus: true });
  setRunning(true);
  status(`Playing ${world} with ${name}. End, or the program's own way out, goes back to the World Editor.`);
});

// A recipe's Make (docs/BLIP_RECIPES_DESIGN.md, blip_recipes.bb in the World
// Editor): Blip is asked to make it, with his panel open, as soon as he is
// free.
async function makeRecipeForWorldEditor(request) {
  const reply = (ok, error) => runner.pageMessage({ protocol: "blitz3d.editor-host", version: 1, type: "recipe-asked",
    id: request.id || "", ok, ...(error ? { error } : {}) });
  if (!blip || typeof request.text !== "string" || !request.text) return reply(false, "Blip is not available here.");
  const result = await blip.askWhenFree(request.text, { label: request.label || null });
  reply(result.ok, result.error);
}

// An interior's build (docs/BLIP_INTERIORS_DESIGN.md, scene_interiors.bb in
// the World Editor): the editor asks for its program, which builds the
// interior with blitz.maplet, to be run with B3D_MAPLET_OUTPUT naming the
// interior's folder. It runs hidden, in a run frame of its own, so the editor
// stays open. The module sends each file it writes ("protocol":
// "blitz.maplet"), which goes into the project and to the editor, and the
// editor is told when the program ends, with what it printed.
let interiorBuild = null;
const buildInteriorForWorldEditor = attempt("The interior could not be built", async (request) => {
  const reply = (ok, output) => runner.pageMessage({ protocol: "blitz3d.editor-host", version: 1, type: "interior-built",
    id: request.id, ok, output });
  const program = appPath(request.program);
  if (!project || !state.ready || !program) return reply(false, `The program ${request.program} is not in this project.`);
  if (interiorBuild) return reply(false, "An interior is being built already.");
  // The editor saves the program's folder's files just before it asks.
  await toolFiles;
  const { files } = await sources();
  const result = await compile(files, program, "extended", false);
  if (!result.ok) {
    return reply(false, result.errors.map((error) => `${error.file}:${error.row}:${error.col}: ${error.message}`).join("\n") ||
      result.output);
  }
  if (!canRun) return reply(false, "This browser cannot run the interior's program.");
  const pane = document.createElement("div");
  pane.style.cssText = "position: fixed; left: -10000px; top: 0; width: 640px; height: 400px;";
  document.body.append(pane);
  let printed = "";
  const kept = [];
  const builder = interiorBuild = new Runner({ pane, runPath: runner.runPath, runOriginFor: (n) => runner.runOriginFor(n + 7),
    pinned: true, events: {
      started() {}, firstFrame() {}, responding() {}, debug() {}, file() {}, close() {}, float() {},
      output(lines) {
        for (const line of lines) printed += `${line.text}\n`;
      },
      pageMessage(message, file) {
        if (!message || message.protocol !== "blitz.maplet" || message.type !== "file" || !file || !file.data) return;
        const path = typeof message.path === "string" ? message.path.replace(/\\/g, "/").replace(/^\/app\//, "") : "";
        if (!path || path.startsWith("/") || path.includes("..")) return;
        const bytes = new Uint8Array(file.data);
        kept.push(project.write(path, bytes).then(() => runner.putFile(path, bytes)));
      },
      ended(code, message) {
        if (message) printed += `${message}\n`;
        Promise.all(kept).catch(() => {}).then(() => {
          tree.show(project, tree.selected);
          builder.stop();
          pane.remove();
          interiorBuild = null;
          reply(!message, printed);
        });
      },
    } });
  const slash = program.lastIndexOf("/");
  // The module's .build.cache, which runs leave out with other hidden files,
  // lets a rebuild start from the shapes that did not change.
  const assets = await packAssets(project.paths().map((path) => [path, project.blob(path)]), project.main,
    (path) => /(^|\/)\.build\.cache$/.test(path));
  builder.run({ title: "Interior", debug: false, wasm: result.wasm, assets,
    launchDirectory: slash >= 0 ? program.slice(0, slash) : ".", focus: false, openUrls: false,
    environment: { B3D_MAPLET_OUTPUT: String(request.output || "").replace(/\\/g, "/") } });
});

// The program the World Editor played has ended: the editor opens again on
// its world, with the same program to play.
function returnToWorldEditor(record) {
  if (state.lastRun !== record || !record.play) return;
  record.play.returned = true;
  openWorldEditor(record.play.world, record.play.program);
}

// The World Editor says which world it has open, as the IDE started it or as
// it opened one itself (File > Open, New or Save As): Blip's world tools and
// the card of his changes follow it.
function worldOpened(message) {
  const record = state.lastRun;
  if (!record || !record.tool) return;
  record.world = typeof message.file === "string" && message.file ? appPath(message.file) : null;
}

// In a buddy session, the World Editor's world is shared when the session has
// it: the editor's edits go through the room, which puts everyone's in one
// order (buddies/world.js). As the editor opens, and as a session starts or
// ends while it is open, the relay is made or closed; a new one asks the
// editor which world it has open when report is true.
function syncSharedWorld(report = false) {
  const session = buddies && buddies.session;
  const record = state.lastRun;
  const editorOpen = worldEditorOpen() && record && project && record.projectId === project.id;
  const usable = editorOpen && session && session.project === project && !session.stopped;
  if (sharedWorld && (!usable || sharedWorld.session !== session)) {
    sharedWorld.close();
    sharedWorld = null;
  }
  if (!usable || sharedWorld) return;
  sharedWorld = shareWorld(session, (message) => runner.pageMessage(message), {
    refused: (details) => buddies.offerCombine(details),
    worldOf: (file) => appPath(file),
    settle: () => toolFiles,
    // A terrain stroke's heights or pixels from the room, in the editor's files.
    putFile: (path, bytes) => runner.putFile(path, bytes),
  });
  if (report) sharedWorld.report();
}

function toolClosed() {
  document.body.classList.remove("tool-running");
  hideBesideTool();
  if (sharedWorld) {
    sharedWorld.close();
    sharedWorld = null;
  }
  $("stop").textContent = "End";
  $("stop").title = "End the program";
  $("stop").classList.remove("labelled");
  editorCommands();
  if (blip) blip.worldEditorChanged();
}

function setRunning(running) {
  $("stop").disabled = !running;
  $("fullscreen").disabled = !running;
  // The web World Editor cannot show a running program's world, so every
  // way to open it waits for the program to end rather than offering to.
  programRunning = running && !(state.lastRun && state.lastRun.tool);
  for (const id of ["tool-world", "world-editor"]) $(id).disabled = programRunning;
  $("tool-world").title = programRunning ? WORLD_WAITS :
    "New world in the World Editor: models, lights and physics, saved with this program's files";
  if (programRunning) clearNotices("world");
}

// The toolbar's editing commands work on the text editor, while it shows,
// rather than the help, a preview or the World Editor.
function editorShown() {
  return $("preview").hidden && $("help-pane").hidden && $("modules-pane").hidden &&
    !document.body.classList.contains("tool-running");
}
function editorCommands() {
  const off = !editorShown();
  for (const id of ["tool-cut", "tool-copy", "tool-paste", "tool-find", "menu-find", "menu-find-next",
    "menu-find-previous", "menu-replace", "menu-go-to-line", "menu-comment", "menu-indent", "menu-outdent"]) {
    $(id).disabled = off;
  }
}

// The project's path for a file a program names, which may spell it in
// another case, as Include "Lib\Helper.bb" names lib/helper.bb.
function projectPath(file) {
  if (!file) return null;
  const name = file.replace(/\\/g, "/");
  if (project.has(name)) return name;
  const lower = name.toLowerCase();
  return project.paths().find((path) => path.toLowerCase() === lower) || null;
}

// Shows a place in a file: opened, with the cursor there.
async function showLocation(file, row, col) {
  const path = projectPath(file);
  if (!path) return;
  await openFile(path);
  editor.goTo(row, col);
}

// Marks errors in their files, and shows the first unless show is false.
async function markErrors(errors, show = true) {
  const byPath = new Map();
  let first = null;
  for (const error of errors) {
    const path = projectPath(error.file);
    if (!path || !TEXT.test(path)) continue;
    if (!byPath.has(path)) byPath.set(path, []);
    byPath.get(path).push(error);
    editor.ensure(path, await project.text(path));
    first ??= { path, error };
  }
  editor.showErrors(byPath);
  if (first && show) await showLocation(first.path, first.error.row, first.error.col);
}

// The Include an "Unable to open include file" error names, when it reaches
// outside the project's folder.
async function includeOutside(error, files) {
  const path = projectPath(error.file);
  if (!/include file/i.test(error.message) || !path || files[path] === undefined) return null;
  const line = files[path].split(/\r?\n/)[error.row - 1] || "";
  const match = /Include\s+"([^"]+)"/i.exec(line);
  if (!match) return null;
  const folder = error.file.split("/").slice(0, -1);
  let depth = folder.length;
  for (const part of match[1].replace(/\\/g, "/").split("/")) {
    if (part === "..") --depth;
    else if (part && part !== ".") ++depth;
    if (depth < 0) return match[1];
  }
  return null;
}

// The asset package, kept while no file in it changes, nor the file that
// runs, whose folder is never an earlier web build. Sources are not in the
// package, so editing them keeps it. A project's files are Blobs, replaced
// when written, so the same Blobs mean the same contents.
let assetCache = null;
const assetFiles = () => project.paths().filter((path) => !skipAsset(path)).map((path) => [path, project.blob(path)]);
const sameFiles = (a, b) => a.length === b.length && a.every(([path, blob], k) => path === b[k][0] && blob === b[k][1]);
async function projectAssets() {
  const files = assetFiles(), main = project.main;
  if (!assetCache || assetCache.project !== project.id || assetCache.main !== main || !sameFiles(assetCache.files, files)) {
    assetCache = { project: project.id, main, files, assets: await packAssets(files, main) };
  }
  return assetCache.assets;
}

// What a build compiles: the main file and the text of each text file, with
// the editor's unsaved text, but not earlier web builds (runner.js).
async function sources() {
  const texts = editor.texts();
  const builds = await earlierWebBuilds(project.paths().map((path) => [path, project.blob(path)]), project.main);
  const files = {};
  for (const path of project.paths()) {
    if (TEXT.test(path) && !builds.has(path)) files[path] = texts.get(path) ??
      (buddies && buddies.textOf(path)) ?? await project.text(path);
  }
  return { main: project.main, files };
}

// Compiles the project. Returns { title, wasm, assets, launchDirectory, loads },
// loads being what the program loads by name (compileProgram in
// compiler/compile.mjs);
// { failed: true, errors } when it has errors, after showing them; or null
// after saying which file to run. quiet only marks errors in the editor,
// for a build the user did not ask for (Live): the console keeps the
// program's output, and the cursor stays where the user is typing. exported
// is a build to download, which is not a live build even with Live on.
async function build(debug, { quiet = false, exported = false, file = null } = {}) {
  await saveAll();
  const main = file || project.main;
  if (!main || !project.has(main)) {
    status("Choose the file to run: double-click it in the file list.");
    return null;
  }
  const { files } = await sources();
  const started = performance.now();
  // With Live on, a live build, whose numbers can change while it runs.
  const result = await compile(files, main, "extended", debug, live.on && !exported);
  const compileMs = performance.now() - started;
  if (!result.ok && quiet) {
    await markErrors(result.errors, false);
    return { failed: true, errors: result.errors, compileMs };
  }
  if (!result.ok) {
    status(result.errors.length ? "The program has an error." : "The program could not be compiled.");
    consoleElement.replaceChildren();
    for (const error of result.errors) {
      writeLocation(error.file, error.row, error.col, error.message);
      const outside = await includeOutside(error, files);
      if (outside) {
        write(`"${outside}" is outside this program's files, which cannot reach beyond their folder. Import the folder ` +
          "that holds both files, or add the file here and change the Include to match.", "note");
      }
    }
    if (!result.errors.length) write(result.output, "error");
    await markErrors(result.errors);
    if (blip && result.errors.length) blip.offer({ kind: "compile", errors: result.errors });
    return { failed: true, errors: result.errors, compileMs };
  }
  editor.showErrors(new Map());
  // Shader assets with HLSL source are translated to WGSL by the server.
  if (account) {
    await account.prepareShaders(project);
    tree.show(project);
  }
  const slash = main.lastIndexOf("/");
  return { title: project.name, wasm: result.wasm, assets: await projectAssets(), compileMs,
    liveNumbers: result.liveNumbers || [], loads: result.loads || [],
    launchDirectory: slash < 0 ? "." : main.slice(0, slash) };
}

// Live (docs/ONLINE_IDE_LIVE_CODE.md, Phase A): with it on, a program that
// has been run restarts with the user's changes a moment after they stop
// typing. While the code has errors, the program keeps running the last code
// that worked, and the errors are marked where they are.
const LIVE_DELAY_MS = 400;
const live = state.live = {
  on: false,
  // Whether a program has been run since the last Stop: Live keeps it up to
  // date, and does not start one the user stopped.
  active: false,
  timer: 0,
  // Counts runs and edits, so a build that a newer one replaces is dropped.
  generation: 0,
  // What the program running was built from.
  ran: null,
  // A live build's numbers, in the text it was built from (livenumbers.js):
  // Map path -> spans. Null when the program running was not a live build.
  numbers: null,
  restarts: 0, unchanged: 0, notRestarted: 0, numberChanges: 0,
};

// What a build depends on: the sources, the main file, the language mode,
// Debug, and the asset files.
const buildInputs = async (debug) => {
  const { main, files } = await sources();
  return { settings: JSON.stringify([project.id, main, "extended", debug]), files, assets: assetFiles() };
};
const sameText = (a, b) => Object.keys(a).length === Object.keys(b).length &&
  Object.keys(a).every((path) => a[path] === b[path]);
const sameInputs = (a, b) => !!a && !!b && a.settings === b.settings && sameText(a.files, b.files) &&
  sameFiles(a.assets, b.assets);

// An edit that changed only numbers in a live build that is running (Phase
// B): numberChange's result, or null.
function numbersOnly(inputs) {
  const ran = live.ran;
  // A program stopped at its error starts again instead.
  const atError = debugPane.stopped && debugPane.stopped.reason === "error";
  if (!live.numbers || !runner.running || atError || !ran || ran.settings !== inputs.settings ||
      !sameFiles(ran.assets, inputs.assets)) return null;
  return numberChange(ran.files, inputs.files, live.numbers);
}

// Something a build depends on changed. quick, for a number dragged or a
// colour picked, tries to change the running program at once, and otherwise
// waits as for typing.
function liveChanged(quick = false) {
  if (!live.on || !live.active || !state.ready || !project || document.body.classList.contains("tool-running")) return;
  clearTimeout(live.timer);
  ++live.generation;
  live.timer = setTimeout(() => run({ live: true, quick }), quick ? 0 : LIVE_DELAY_MS);
}

// The program was stopped, or put away: Live waits for the next run.
function liveIdle() {
  clearTimeout(live.timer);
  ++live.generation;
  live.active = false;
}

// stopAtStart: stop at the first line, in a debug build. isLive: a restart
// that Live started, which leaves the program running when the code has
// errors, and leaves the keyboard and the tab shown where they are. quick:
// Live only changes the running program's values, and otherwise waits.
async function run({ stopAtStart = false, live: isLive = false, quick = false } = {}) {
  if (!state.ready || !project || (!isLive && $("run").disabled)) return;
  // Live leaves an online test's player as it is.
  if (isLive && onlineTest) return;
  if (!isLive) endCheck("stopped");
  clearTimeout(live.timer);
  const generation = ++live.generation;
  const debug = $("debug").checked;
  let record = null;
  if (!isLive) {
    if (stopRunner()) write("Stopped.", "note");
    debugPane.ended();
    toolClosed();
    setRunning(false);
    record = state.lastRun = { started: performance.now(), compileMs: null, firstFrameMs: null,
      firstOutputMs: null, endedMs: null, ended: null, debugEvents: [] };
    ++state.runs;
    consoleElement.replaceChildren();
    consoleView.conceal();
    $("run").disabled = true;
    status("Compiling…");
  }
  const started = performance.now();
  const hadProgram = !!runner.frame;
  let built, inputs, restartReason = null;
  try {
    await saveAll();
    inputs = await buildInputs(debug);
    if (isLive && hadProgram && sameInputs(inputs, live.ran)) {
      // Back to the code that is running, as after an undo, or a drag that
      // has already changed it.
      ++live.unchanged;
      editor.showErrors(new Map());
      if ($("status").textContent.startsWith("Not ")) status("The program is running this code.");
      return;
    }
    const numbers = isLive && hadProgram ? numbersOnly(inputs) : null;
    if (numbers && numbers.reason === null) {
      // Only numbers changed: the running program takes them as it is.
      if (generation !== live.generation) return;
      runner.liveNumbers(numbers.writes);
      live.ran = inputs;
      live.numbers = numbers.spans;
      ++live.numberChanges;
      editor.showErrors(new Map());
      for (const path of new Set(numbers.changed.map((change) => change.path))) {
        editor.flashNumbers(path, numbers.changed.filter((change) => change.path === path)
          .map((change) => [change.from, change.to]));
      }
      const count = numbers.changed.length;
      status(count === 1 ? `Changed ${numbers.changed[0].before} to ${numbers.changed[0].after} in the running program.` :
        `Changed ${count} values in the running program.`);
      return;
    }
    restartReason = numbers && numbers.reason || null;
    if (quick) {
      // Not while a number is dragged: once it rests.
      liveChanged();
      return;
    }
    built = await build(debug, { quiet: isLive });
  } finally {
    if (!isLive) $("run").disabled = false;
  }
  // A newer edit or run replaces this one.
  if (generation !== live.generation) return;
  if (isLive) {
    if (!built) return;
    if (built.failed) {
      ++live.notRestarted;
      const count = built.errors.length;
      const not = hadProgram ? "Not restarted" : "Not started";
      status(count ? `${not}: ${count} error${count === 1 ? "" : "s"}, marked in the code.` +
        (hadProgram ? " The program runs the last code that worked." : "") :
        `${not}: the program could not be compiled.`);
      return;
    }
    if (stopRunner()) debugPane.ended();
    setRunning(false);
    record = state.lastRun = { started, compileMs: null, firstFrameMs: null, firstOutputMs: null,
      endedMs: null, ended: null, debugEvents: [], live: true, restarted: hadProgram, reason: restartReason };
    ++state.runs;
    consoleElement.replaceChildren();
    consoleView.conceal();
  }
  record.compileMs = performance.now() - record.started;
  record.compiled = !!built && !built.failed;
  if (!built) {
    record.ended = { status: "no-main" };
    return;
  }
  if (built.failed) {
    record.ended = { status: "compile-error", errors: built.errors };
    return;
  }
  if (!canRun) {
    status("The program compiles, but this browser cannot run it.");
    record.ended = { status: "cannot-run" };
    return;
  }
  if (!isLive) {
    // A docked program shows in the pane from the start; others once they
    // say how they show (float, and firstFrame for a GUI program).
    if (runner.window.docked) layout.showProgram(true);
    status(`Starting… (compiled in ${Math.round(record.compileMs)} ms)`);
  }
  // A debug build serves the debugger.
  if (debug) debugPane.start({ stopAtStart });
  else debugPane.newRun();
  runner.run({ title: built.title, debug, wasm: built.wasm, assets: built.assets,
    launchDirectory: built.launchDirectory, storageKey: `ide|${project.id}`, attach: debug, focus: !isLive });
  setRunning(true);
  live.ran = inputs;
  live.numbers = built.liveNumbers.length ? numberSpans(inputs.files, built.liveNumbers) : null;
  live.active = true;
  if (isLive) ++live.restarts;
}

// Stops the program running, and an online test's server with it. Returns
// whether either ran.
function stopRunner() {
  const ended = endOnlineTest();
  return runner.stop() || ended;
}

// The online test (docs/GAME_SERVERS_DESIGN.md, "Testing on your own
// computer"; onlinetest.js): the program as a Shared World server room in a
// run frame no one sees, and once its room is open, as a player who joins it
// here, as Run runs it. Its service is Shared World's own, unless the page's
// address names another (?shared-service=, for tests).
let onlineTest = null;
const sharedService = params.get("shared-service") || ONLINE_SERVICE;

// Program > Run online test…'s choices, from those last made: {bots,
// network}, or null for Cancel.
function chooseOnlineTest() {
  let last = {};
  try { last = JSON.parse(remember.get(ONLINE_TEST) || "{}") || {}; } catch {}
  const dialog = $("online-dialog"), bots = $("online-bots"), network = $("online-network");
  if (!bots.options.length) {
    for (const choice of ONLINE_BOTS) bots.append(new Option(choice.label, String(choice.value)));
    for (const choice of ONLINE_NETWORKS) network.append(new Option(choice.label, choice.name));
  }
  bots.value = ONLINE_BOTS.some((choice) => choice.value === last.bots) ? String(last.bots) : "-1";
  network.value = ONLINE_NETWORKS.some((choice) => choice.name === last.network) ? last.network : "none";
  const chosen = () => ({ bots: Number(bots.value), network: network.value });
  const describe = () => { $("online-hint").textContent = describeOnlineTest(chosen()); };
  bots.onchange = network.onchange = describe;
  describe();
  return new Promise((resolve) => {
    dialog.returnValue = "";
    dialog.addEventListener("close", () => {
      if (dialog.returnValue !== "run") return resolve(null);
      remember.set(ONLINE_TEST, JSON.stringify(chosen()));
      resolve(chosen());
    }, { once: true });
    dialog.showModal();
  });
}

// Runs an online test of the file Run starts, or of file: compiled as Run
// compiles it, with the program's bots, or bots keeping a room that full,
// and a network of ONLINE_NETWORKS. Returns what started, or throws why not.
async function runOnlineTest({ bots = -1, network = "none", file = null } = {}) {
  if (!state.ready || !project) throw new Error("No program is open");
  if (!canRun) throw new Error("This browser cannot run programs");
  endCheck("stopped");
  liveIdle();
  if (stopRunner()) write("Stopped.", "note");
  debugPane.ended();
  toolClosed();
  setRunning(false);
  const debug = $("debug").checked;
  const record = state.lastRun = { started: performance.now(), compileMs: null, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], online: { bots, network, joined: false } };
  ++state.runs;
  consoleElement.replaceChildren();
  consoleView.conceal();
  status("Compiling…");
  // Not a live build: Live leaves an online test's player as it is.
  const built = await build(debug, { exported: true, file });
  record.compileMs = performance.now() - record.started;
  if (state.lastRun !== record) throw new Error("Another run replaced it");
  if (!built) throw new Error("There is no program to test: choose the file Run starts");
  if (built.failed) {
    record.ended = { status: "compile-error", errors: built.errors };
    throw new Error("The program has an error, marked in the code");
  }
  startOnlineTest({ ...built, debug, commandLine: "", bots, network, record });
  return { program: file || project.main, players: 1, bots, network };
}

// Starts an online test's server with a program compiled already: { title,
// wasm, assets, launchDirectory, commandLine, debug, bots, network, record }.
// The player joins once the server's room is open (joinOnlineTest).
function startOnlineTest({ title, wasm, assets, launchDirectory, commandLine, debug, bots, network, record }) {
  endOnlineTest();
  const test = onlineTest = new OnlineTest({ runPath: runner.runPath, runOriginFor: (n) => runner.runOriginFor(n + 9),
    service: sharedService, bots, network, events: {
      output: (text, error) => { if (onlineTest === test) write(text, error ? "error" : ""); },
      status: (text) => { if (onlineTest === test) status(text); },
      roomOpened: () => { if (onlineTest === test) joinOnlineTest(); },
      failed: () => {
        if (onlineTest !== test) return;
        onlineTest = null;
        // The console shows why; End, or a run, goes on from there.
        record.ended = { status: "server-stopped" };
        setRunning(false);
      },
    } });
  test.record = record;
  test.program = { title, wasm, assets, launchDirectory, commandLine, debug };
  test.start({ title, wasm, assets, launchDirectory, commandLine });
  setRunning(true);
  status("Starting the online test's server…");
}

// The player joins the server's room: as Run runs the program, in the
// program pane, debugger and all.
function joinOnlineTest() {
  const test = onlineTest, program = test.program;
  test.record.online.joined = true;
  if (runner.window.docked) layout.showProgram(true);
  if (program.debug) debugPane.start({});
  else debugPane.newRun();
  runner.run({ title: program.title, debug: program.debug, wasm: program.wasm, assets: program.assets,
    launchDirectory: program.launchDirectory, storageKey: `ide|${project.id}`, attach: program.debug,
    commandLine: program.commandLine, environment: test.environment(1), focus: true });
  setRunning(true);
}

// Ends the online test's server. Returns whether one ran.
function endOnlineTest() {
  if (!onlineTest) return false;
  const test = onlineTest;
  onlineTest = null;
  return test.end();
}

// Runs files given, rather than the project's as the editor has them: a buddy
// session's Run for everyone (buddies/panel.js) runs exactly the files the
// host ran, on each screen that allows it. files: [[path, Blob], ...].
async function runFiles({ files, main, mode }) {
  if (!state.ready || !project || !main) return;
  endCheck("stopped");
  liveIdle();
  if (stopRunner()) write("Stopped.", "note");
  debugPane.ended();
  toolClosed();
  setRunning(false);
  const record = state.lastRun = { started: performance.now(), compileMs: null, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], everyone: true };
  ++state.runs;
  consoleElement.replaceChildren();
  consoleView.conceal();
  status("Compiling…");
  const texts = {};
  for (const [path, blob] of files) if (TEXT.test(path)) texts[path] = await blob.text();
  const result = await compile(texts, main, mode, false);
  record.compileMs = performance.now() - record.started;
  if (!result.ok) {
    status("The program has an error.");
    for (const error of result.errors) writeLocation(error.file, error.row, error.col, error.message);
    if (!result.errors.length) write(result.output, "error");
    record.ended = { status: "compile-error", errors: result.errors };
    return;
  }
  if (!canRun) {
    status("The program compiles, but this browser cannot run it.");
    record.ended = { status: "cannot-run" };
    return;
  }
  const assets = await packAssets(files.filter(([path]) => !skipAsset(path)), main);
  if (runner.window.docked) layout.showProgram(true);
  const slash = main.lastIndexOf("/");
  runner.run({ title: project.name, debug: false, wasm: result.wasm, assets,
    launchDirectory: slash < 0 ? "." : main.slice(0, slash), storageKey: `ide|${project.id}`, attach: false, focus: true });
  setRunning(true);
}

// F5 continues a stopped program, and otherwise runs; F10, F11 and
// Shift+F11 step it. F10 and F11 with no program running start one stopped at
// its first line, turning Debug on.
function runKey(key) {
  if (debugPane.key(key)) return true;
  if (key === "F5") {
    run();
    return true;
  }
  if ((key === "F10" || key === "F11") && !runner.running && state.ready) {
    if (!$("debug").checked) {
      $("debug").checked = true;
      remember.set(DEBUG, "1");
    }
    run({ stopAtStart: true });
    return true;
  }
  return false;
}

// Ends Blip's test run's wait, when the user runs or stops a program.
function endCheck(status) {
  const check = state.lastRun && state.lastRun.check;
  if (check && check.resolve) check.resolve({ status });
}

// Blip's compile (blip/tools.js): as Run compiles, without running or
// showing anything, the main file, or another program of the project (file).
async function blipCompile(file = null) {
  const main = file || (project && project.main);
  if (!project || !main || !project.has(main)) {
    return { ok: false, errors: [], output: "No file is chosen to run: double-click it in the file list." };
  }
  const { files } = await sources();
  return compile(files, main, "extended", false);
}

// Blip's test run (blip/tools.js, run_program): a debug build, so that a
// runtime error comes with its line, run in the program pane without taking
// the keyboard or opening web pages. The runtime's automatic capture
// (B3D_CAPTURE_FILE, B3D_CAPTURE_FRAME) stops it at the frame asked for and
// keeps that frame; B3D_CAPTURE_KEYS presses keys at chosen frames, and
// MilliSecs follows the frame count (B3D_CAPTURE_FRAME_CLOCK), so that a run
// repeats exactly. While the World Editor is open, it only compiles: a run
// would take the editor's place and lose the user's unsaved changes there.
// It runs the main file, or another program of the project (file).
// Returns { status: captured, ended, error, not-responding, timeout, hidden,
// cannot-run, editor-open, stopped or compile-error, message, location,
// capture, output, drew, noGpu }.
async function checkRun({ frames = 60, keys = [], signal = null, file = null } = {}) {
  const main = file || (project && project.main);
  if (!state.ready || !project || !main || !project.has(main)) {
    return { status: "compile-error", errors: [], output: "No file is chosen to run." };
  }
  if (document.body.classList.contains("tool-running")) {
    const result = await blipCompile(main);
    if (!result.ok) return { status: "compile-error", errors: result.errors, output: result.output };
    status("Blip did not try your program, as the World Editor is open.");
    return { status: "editor-open" };
  }
  endCheck("stopped");
  clearTimeout(live.timer);
  ++live.generation;
  if (stopRunner()) write("Stopped.", "note");
  debugPane.ended();
  toolClosed();
  setRunning(false);
  const record = state.lastRun = { started: performance.now(), compileMs: null, firstFrameMs: null,
    firstOutputMs: null, endedMs: null, ended: null, debugEvents: [], check: { output: [], resolve: null } };
  ++state.runs;
  consoleElement.replaceChildren();
  consoleView.conceal();
  status("Blip is compiling your program to try it…");
  await saveAll();
  const { files } = await sources();
  const result = await compile(files, main, "extended", true);
  record.compileMs = performance.now() - record.started;
  if (!result.ok) {
    record.ended = { status: "compile-error", errors: result.errors };
    status("Blip's test build has errors.");
    return { status: "compile-error", errors: result.errors, output: result.output };
  }
  if (!canRun) {
    record.ended = { status: "cannot-run" };
    return { status: "cannot-run" };
  }
  if ((signal && signal.aborted) || state.lastRun !== record) return { status: "stopped" };
  // A hidden page draws nothing, so the run could only wait out its time.
  if (document.visibilityState === "hidden") {
    record.ended = { status: "hidden" };
    status("Blip could not try your program while this tab was hidden.");
    return { status: "hidden", frames, output: [], drew: false, noGpu: !navigator.gpu };
  }
  const environment = { B3D_CAPTURE_FILE: "/tmp/blip-capture.png", B3D_CAPTURE_FRAME: String(frames),
    B3D_CAPTURE_FRAME_CLOCK: "1" };
  if (keys.length) environment.B3D_CAPTURE_KEYS = keys.map((key) => `${key.frame}:${key.scancode}`).join(",");
  const outcome = new Promise((resolve) => {
    record.check.resolve = (value) => {
      record.check.resolve = null;
      resolve(value);
    };
  });
  if (runner.window.docked) layout.showProgram(true);
  const slash = main.lastIndexOf("/");
  runner.run({ title: project.name, debug: true, wasm: result.wasm, assets: await projectAssets(),
    launchDirectory: slash < 0 ? "." : main.slice(0, slash), storageKey: `ide|${project.id}`, attach: false,
    focus: false, environment, openUrls: false });
  setRunning(true);
  // Loading the runtime takes a few seconds, and frames come at about 60 a
  // second.
  const limit = setTimeout(() => {
    if (record.check.resolve) record.check.resolve({ status: document.visibilityState === "hidden" ? "hidden" : "timeout" });
  }, 20000 + frames * 50);
  const aborted = () => { if (record.check.resolve) record.check.resolve({ status: "stopped" }); };
  if (signal) signal.addEventListener("abort", aborted, { once: true });
  const ended = await outcome;
  clearTimeout(limit);
  if (signal) signal.removeEventListener("abort", aborted);
  // Still running, and not replaced by a run of the user's: stopped here.
  if (state.lastRun === record && !record.ended) {
    liveIdle();
    debugPane.ended();
    layout.showProgram(false);
    if (stopRunner()) write("Stopped.", "note");
    setRunning(false);
    $("run-pane").replaceChildren(placeholder);
    record.ended = { status: ended.status };
    status(ended.status === "stopped" ? "Blip's test run was stopped." : "Blip stopped his test run.");
  }
  return { ...ended, frames, output: record.check.output, drew: record.firstFrameMs !== null, noGpu: !navigator.gpu };
}

function stop() {
  endCheck("stopped");
  liveIdle();
  debugPane.ended();
  toolClosed();
  layout.showProgram(false);
  const stopped = stopRunner();
  if (stopped) {
    write("Stopped.", "note");
    status("Stopped.");
  }
  setRunning(false);
  $("run-pane").replaceChildren(placeholder);
  // Ending a program the World Editor played goes back to the editor.
  const record = state.lastRun;
  if (stopped && record && record.play && !record.ended) {
    record.ended = { status: "stopped" };
    returnToWorldEditor(record);
    return;
  }
  editor.focus();
}

const placeholder = $("run-pane").firstElementChild;
$("run").addEventListener("click", () => run());
$("stop").addEventListener("click", () => {
  if (state.lastRun && state.lastRun.tool === "world-editor" && !state.lastRun.ended &&
      !confirm("Close the World Editor? Changes you have not saved are lost.")) return;
  stop();
});
$("world-editor").addEventListener("click", () => {
  closeMenu();
  openWorldEditor(null);
});
$("tool-world").addEventListener("click", () => openWorldEditor(null));
$("fullscreen").addEventListener("click", () => runner.fullscreen());
// Turning Live on brings the program up to date, starting it when none is
// shown; the keyboard stays where it was.
$("live").checked = live.on = remember.get(LIVE) === "1";
$("live").addEventListener("change", () => {
  live.on = $("live").checked;
  remember.set(LIVE, live.on ? "1" : "0");
  if (!live.on) {
    clearTimeout(live.timer);
    ++live.generation;
    status("Live is off: Run (F5) runs your changes.");
  } else if (document.body.classList.contains("tool-running")) {
    status("Live restarts programs, not the World Editor.");
  } else if (runner.frame) {
    live.active = true;
    liveChanged();
  } else {
    run({ live: true });
  }
});
// The editor handles its own keys, and these elsewhere in the page.
addEventListener("keydown", (event) => {
  if (event.defaultPrevented) return;
  if (event.key === "F5" || (event.key === "Enter" && (event.ctrlKey || event.metaKey))) {
    event.preventDefault();
    if (event.key === "F5") runKey("F5");
    else run();
  } else if (event.key === "F10" || event.key === "F11") {
    const key = event.shiftKey ? `Shift-${event.key}` : event.key;
    if (runKey(key)) event.preventDefault();
  } else if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "b") {
    event.preventDefault();
    layout.toggle();
  } else if (event.ctrlKey && !event.altKey && !event.shiftKey && event.code === "Backquote") {
    event.preventDefault();
    consoleView.toggle();
  } else if (event.ctrlKey && !event.altKey && !event.metaKey && ["-", "=", "+", "0"].includes(event.key) &&
    (event.key !== "0" || !event.shiftKey)) {
    // The code's size rather than the page's zoom, as in the Windows IDE.
    event.preventDefault();
    changeFontSize(event.key === "0" ? MEDIUM : fontSize() + (event.key === "-" ? -1 : 1));
  } else if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "r") {
    // Replace… and Replace in files, as in the Windows IDE, and never the
    // browser's reload. An open dialog keeps the keys.
    event.preventDefault();
    if (document.querySelector("dialog[open]")) return;
    closeMenu();
    if (event.shiftKey) showFindInFiles(true);
    else if (editorShown()) editor.replace();
  } else if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === "f" &&
    (event.shiftKey || editorShown())) {
    // Find in files, and Find… while the editor shows: elsewhere, as in the
    // help, the browser's own find.
    event.preventDefault();
    if (document.querySelector("dialog[open]")) return;
    closeMenu();
    if (event.shiftKey) showFindInFiles();
    else editor.find();
  } else if (event.key === "F3" && !event.ctrlKey && !event.altKey && !event.metaKey && editorShown()) {
    event.preventDefault();
    if (document.querySelector("dialog[open]")) return;
    closeMenu();
    if (event.shiftKey) editor.findPrevious();
    else editor.findNext();
  } else if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "i" && blip) {
    event.preventDefault();
    blip.toggle();
  }
});

// The toolbar's Cut, Copy, Paste and Find, as the desktop IDE's, for the text
// editor. The browser asks before a page first reads the clipboard.
$("tool-cut").addEventListener("click", attempt("The text could not be cut", async () => {
  const text = editor.selectedText;
  if (text) {
    await navigator.clipboard.writeText(text);
    editor.replaceSelection("");
  }
  editor.focus();
}));
$("tool-copy").addEventListener("click", attempt("The text could not be copied", async () => {
  const text = editor.selectedText;
  if (text) await navigator.clipboard.writeText(text);
  editor.focus();
}));
$("tool-paste").addEventListener("click", async () => {
  let text;
  try {
    text = await navigator.clipboard.readText();
  } catch {
    status("This browser did not let the page read the clipboard: press Ctrl+V to paste.");
    editor.focus();
    return;
  }
  editor.replaceSelection(text);
});
$("tool-find").addEventListener("click", () => editor.find());

// The files and the program pane beside the editor, which their × buttons,
// View > Files and View > Program and help hide. The full-width editor
// (View > Full-width editor, the toolbar's button, or Ctrl+B) hides both, and
// the console, as the Windows and Mac IDEs' full-width editors do. A link's
// shared program opens in it.
// A hidden program pane shows beside the editor while it is wanted: while a
// program runs in it, or a debug run is stopped, and after F1 or the help
// buttons, until its × hides it again.
const layout = {
  files: !document.documentElement.classList.contains("files-hidden"),
  symbols: !document.documentElement.classList.contains("symbols-hidden"),
  // The program shows in the pane: docked, full screen (Esc returns it
  // there), or a GUI program or the World Editor. It takes the functions'
  // place while it runs.
  program: false,
  get wide() { return !this.files && !this.symbols && consoleView.hidden; },
  apply() {
    const root = document.documentElement.classList;
    root.toggle("files-hidden", !this.files);
    root.toggle("symbols-hidden", !this.symbols);
    root.toggle("program-shown", this.program);
    $("full-width").setAttribute("aria-pressed", String(this.wide));
    $("menu-full-width").setAttribute("aria-checked", String(this.wide));
    $("menu-files").setAttribute("aria-checked", String(this.files));
    $("menu-pane").setAttribute("aria-checked", String(this.symbols));
  },
  // The panes the user chose, kept for next time.
  set({ files = this.files, symbols = this.symbols }) {
    this.beforeHelp = null;
    this.files = files;
    this.symbols = symbols;
    this.keep(files, symbols);
    this.apply();
  },
  keep(files, symbols) {
    remember.set(LAYOUT, files && symbols ? "split" : files ? "no-pane" : symbols ? "no-files" : "wide");
  },
  // Find in files: the functions pane, if it was hidden, as the full-width
  // editor hides it, for its Find tab. The Help tab keeps the layout it goes
  // back to, now with the pane.
  showFind() {
    if (this.symbols) return;
    if (this.beforeHelp) {
      this.beforeHelp.symbols = this.symbols = true;
      this.keep(this.beforeHelp.files, true);
      this.apply();
    } else {
      this.set({ symbols: true });
    }
    status("The functions, types, labels and breakpoints are beside the editor.");
  },
  toggle() {
    const wide = !this.wide;
    consoleView.hidden = wide;
    document.documentElement.classList.remove("console-shown");
    remember.set(CONSOLE, wide ? "hidden" : "shown");
    consoleView.apply();
    this.set({ files: !wide, symbols: !wide });
    status(wide ? "The editor has the full width. A program in the pane shows beside it while it runs; " +
      "Ctrl+B brings back the files, the functions and the console." :
      "The files, the functions and the console are beside the editor again.");
  },
  showFiles(shown) {
    this.set({ files: shown });
    status(shown ? "The files are beside the editor." : "The files are hidden. View > Files shows them again.");
  },
  showSymbols(shown) {
    this.set({ symbols: shown });
    status(shown ? "The functions, types, labels and breakpoints are beside the editor." :
      "The functions, types, labels and breakpoints are hidden. View > Functions and types shows them again.");
  },
  showProgram(shown) {
    if (this.program === shown) return;
    this.program = shown;
    this.apply();
  },
  // A link's shared program shows in the full-width editor, as index.html
  // draws it at load. The layout kept for next time stays as it is.
  widen() {
    this.files = false;
    this.symbols = false;
    consoleView.hidden = true;
    document.documentElement.classList.remove("console-shown");
    consoleView.apply();
  },
  // The layout kept for next time, again, after a link whose program could
  // not be read: index.html drew the page full width before reading it.
  restore() {
    const kept = remember.get(LAYOUT);
    // At first, as index.html draws it: the editor and the pane beside it.
    this.files = kept === "split" || kept === "no-pane";
    this.symbols = kept !== "wide" && kept !== "no-pane";
    consoleView.hidden = remember.get(CONSOLE) !== "shown";
    consoleView.apply();
  },
  // The Help tab has the full width; another tab brings back the layout it
  // had before, unless the user changed the layout in the meantime. The
  // layout kept for next time stays as it is.
  beforeHelp: null,
  enterHelp() {
    if (this.beforeHelp || this.wide) return;
    this.beforeHelp = { files: this.files, symbols: this.symbols, console: consoleView.hidden,
      consoleShown: document.documentElement.classList.contains("console-shown") };
    this.widen();
    this.apply();
  },
  leaveHelp() {
    const before = this.beforeHelp;
    if (!before) return;
    this.beforeHelp = null;
    this.files = before.files;
    this.symbols = before.symbols;
    consoleView.hidden = before.console;
    document.documentElement.classList.toggle("console-shown", before.consoleShown);
    consoleView.apply();
    this.apply();
  },
};
layout.apply();
$("full-width").addEventListener("click", () => layout.toggle());
$("menu-full-width").addEventListener("click", () => {
  closeMenu();
  layout.toggle();
  editor.focus();
});
$("menu-find-files").addEventListener("click", () => {
  closeMenu();
  showFindInFiles();
});
$("menu-replace-files").addEventListener("click", () => {
  closeMenu();
  showFindInFiles(true);
});
// The Program and Help menus: the toolbar's commands, and the debugger's and
// the editor's, as the desktop IDEs' menus have them.
for (const [item, control] of MENU_MIRRORS) {
  $(item).addEventListener("click", () => {
    closeMenu();
    if (!$(control).disabled) $(control).click();
  });
}
for (const [item, box] of MENU_CHECKS) {
  $(item).addEventListener("click", () => {
    closeMenu();
    $(box).checked = !$(box).checked;
    $(box).dispatchEvent(new Event("change"));
  });
}
$("menu-run-online").addEventListener("click", async () => {
  closeMenu();
  const choices = await chooseOnlineTest();
  if (choices) runOnlineTest(choices).catch((error) => status(`The online test did not start: ${error.message}.`));
});
$("menu-run-stopped").addEventListener("click", () => {
  closeMenu();
  // A debug run: the debugger stops it before its first line.
  if (!$("debug").checked) {
    $("debug").checked = true;
    remember.set(DEBUG, "1");
  }
  run({ stopAtStart: true });
});
$("menu-breakpoint").addEventListener("click", () => {
  closeMenu();
  editor.toggleBreakpoint();
});
$("menu-clear-breakpoints").addEventListener("click", () => {
  closeMenu();
  editor.clearBreakpoints();
  saveBreakpoints();
  debugPane.breakpointsChanged();
  symbolsChanged();
  status("Removed all the breakpoints.");
  editor.focus();
});
$("menu-quick-help").addEventListener("click", () => {
  closeMenu();
  showHelpFor(editor.wordAtCursor());
});
// The Edit menu's commands for the code, as the desktop IDEs' Edit menus have
// them, on the Windows IDE's keys.
for (const [id, command] of [["menu-find", "find"], ["menu-find-next", "findNext"],
  ["menu-find-previous", "findPrevious"], ["menu-replace", "replace"]]) {
  $(id).addEventListener("click", () => {
    closeMenu();
    editor[command]();
  });
}
for (const [id, name] of [["menu-go-to-line", "goToLine"], ["menu-comment", "comment"], ["menu-indent", "indent"],
  ["menu-outdent", "outdent"]]) {
  $(id).addEventListener("click", () => {
    closeMenu();
    editor.lineCommand(name);
  });
}
$("menu-files").addEventListener("click", () => {
  closeMenu();
  layout.showFiles(!layout.files);
  editor.focus();
});
$("menu-pane").addEventListener("click", () => {
  closeMenu();
  layout.showSymbols(!layout.symbols);
  editor.focus();
});
// Keep program docked: every program starts in the pane (programwindow.js).
const showDocked = () => $("menu-docked").setAttribute("aria-checked", String(runner.window.docked));
showDocked();
$("menu-docked").addEventListener("click", () => {
  closeMenu();
  runner.window.docked = !runner.window.docked;
  showDocked();
  status(runner.window.docked ? "Programs start in the program pane. Full screen ones offer full screen there." :
    "Programs start as their graphics mode asks: in a window of their own, or full screen.");
  editor.focus();
});
$("hide-files").addEventListener("click", () => {
  layout.showFiles(false);
  editor.focus();
});
// The splitters between the panes, and the console's View menu item, its ×
// and Ctrl+`.
const splitters = setupSplitters({ $ });
consoleView.apply();
$("menu-console").addEventListener("click", () => {
  closeMenu();
  consoleView.toggle();
  editor.focus();
});
$("hide-console").addEventListener("click", () => {
  consoleView.toggle(false);
  editor.focus();
});
$("menu-reset-layout").addEventListener("click", () => {
  closeMenu();
  splitters.reset();
  status("The files, the program pane and the console have their first sizes.");
  editor.focus();
});
// The program pane's × ends the program, as its window's does.
$("hide-pane").addEventListener("click", () => {
  stop();
  editor.focus();
});
$("hide-symbols").addEventListener("click", () => {
  layout.showSymbols(false);
  editor.focus();
});
// View > Decrease, Increase and Reset editor font, and Ctrl+-, Ctrl++ and
// Ctrl+0, as in the Windows IDE: the code's three sizes.
function changeFontSize(to) {
  const size = Math.min(Math.max(to, 0), FONT_SIZES.length - 1);
  closeMenu();
  remember.set(FONT_SIZE, String(size));
  setCodeFont();
  editor.refreshFont();
  status(`The code is ${FONT_SIZES[size][0]}.`);
}
$("menu-font-smaller").addEventListener("click", () => { changeFontSize(fontSize() - 1); editor.focus(); });
$("menu-font-larger").addEventListener("click", () => { changeFontSize(fontSize() + 1); editor.focus(); });
$("menu-font-reset").addEventListener("click", () => { changeFontSize(MEDIUM); editor.focus(); });
// View > Line numbers, on unless turned off.
$("menu-line-numbers").setAttribute("aria-checked", String(remember.get(LINE_NUMBERS) !== "off"));
$("menu-line-numbers").addEventListener("click", () => {
  closeMenu();
  const on = remember.get(LINE_NUMBERS) === "off";
  remember.set(LINE_NUMBERS, on ? "on" : "off");
  $("menu-line-numbers").setAttribute("aria-checked", String(on));
  editor.showLineNumbers(on);
  editor.focus();
});
$("menu-blitz-font").addEventListener("click", () => {
  closeMenu();
  remember.set(FONT, blitzFont() ? "modern" : "blitz");
  setCodeFont();
  editor.refreshFont();
  status(blitzFont() ? "Code is in the Blitz font, as in the desktop IDE." : "Code is in a modern font.");
  editor.focus();
});

// The shared asset library: the World Editor's token, the desktop editor's
// sign-in, and the account dialog's sections (library.js).
const library = setupLibrary({ cloud, $, status, notice, ask, accountChanged: () => account && account.refresh() });
// View > Asset library, when the server has the library.
$("menu-asset-library").hidden = !library.available();
$("menu-asset-library").addEventListener("click", () => {
  closeMenu();
  openAssetLibrary();
});

// Accounts, projects in them, share links for projects, and shader
// translation, when the site has its server (account.js).
account = setupAccount({
  accountShown: () => library.showAccount(),
  cloud, store, $, notice, clearNotices, status, write, saveAll, closeMenu,
  project: () => project,
  show: (opened) => showProject(opened),
  // Shows a project again after its files changed underneath the editor.
  reopen: async (changed) => { if (changed === project) await showProject(changed); },
  // Signing in or out lets Blip help, or not.
  accountChanged: () => {
    if (blip) blip.accountChanged();
    if (buddies) buddies.accountChanged();
    if (modules) modules.accountChanged();
  },
});

// Blip, the coding helper, when the server offers him (blip/panel.js). The
// IDE starts without him if he cannot.
try {
  blip = await setupBlip({
    $, cloud, remember, editor, status, closeMenu, help, commands, markErrors, checkRun, removeFile,
    compile: blipCompile,
    // Makes a program the one Run starts, as double-clicking it does.
    setMain: (path) => tree.actions.setMain(path),
    project: () => project,
    mode: () => "extended",
    isText: (path) => TEXT.test(path),
    projectPath: (file) => project && projectPath(file),
    refreshFiles: () => tree.show(project, tree.selected),
    examples: () => examples,
    lastRun: () => state.lastRun,
    running: () => runner.running,
    // The World Editor's world while it is open, path null for a new one, and
    // its run, which is new each time it opens.
    worldEditor: () => worldEditorOpen() ?
      { path: (state.lastRun && state.lastRun.world) || null, run: state.lastRun } : null,
    // Requests to the World Editor's handler, through the run frame
    // (blip/world.js), and opening it as its button does.
    worldRequest: (message, timeout) => runner.request(message, timeout),
    openWorldEditor: async (path) => {
      if (runner.running) return { opened: false, reason: "A program is running: the user can stop it first." };
      if (path && !(project && project.has(path))) return { opened: false, reason: `The project has no world ${path}.` };
      await openWorldEditor(path);
      return worldEditorOpen() ? { opened: true } :
        { opened: false, reason: "The World Editor could not open." };
    },
    openFile: (path) => openFile(path),
    openAccount: () => $("account-button").click(),
    // A model or picture Blip had made (blip/models.js), saved in the project
    // and shown in the Files pane.
    addFile: async (path, bytes) => {
      await project.write(path, bytes);
      tree.show(project, tree.selected);
      liveChanged();
    },
    // Gives the open World Editor a file of the project, which it had not
    // when it opened, so that its import finds it.
    putToolFile: async (path, bytes = null) => {
      const record = state.lastRun;
      if (!project || !project.has(path) || !worldEditorOpen() ||
          !record || record.projectId !== project.id) return false;
      return runner.putFile(path, bytes || await project.bytes(path));
    },
    // His online_test (blip/tools.js): Program > Run online test with his
    // choices, or Play Online Test of a world, which plays it with Player.
    // This browser runs one player; the desktop IDEs run up to four.
    onlineTest: async ({ bots = -1, network = "none", path = null, world = null }) => {
      if (!world) return { ...await runOnlineTest({ bots, network, file: path }), browser: true };
      await playFromWorldEditor({ program: "/b3d/samples/world-editor/play/Player.bb", world, name: "Player", online: true,
        extended: true, bots, network });
      if (!onlineTest || onlineTest.record !== state.lastRun) throw new Error($("status").textContent || "the world could not be played");
      return { program: "Player", players: 1, bots, network, browser: true };
    },
    // A buddy session, whose people Blip answers one at a time.
    buddies: () => buddies && buddies.session,
    blipTurn: (text, signal, waiting, pictures) => buddies ? buddies.blipTurn(text, signal, waiting, pictures) : null,
  });
} catch (error) {
  console.error("Blip could not start:", error);
  blip = null;
}

// The Modules tab, when the server has a module library (modules.js).
try {
  modules = setupModules({
    $, cloud, remember, editor, closeMenu, status,
    project: () => project,
    openFile: (path) => openFile(path),
    showTab: () => showModulesView(),
    refreshFiles: () => { if (project) tree.show(project, tree.selected); },
    // A file the Modules page wrote or removed: the editor drops its old text.
    fileChanged: (path, removed = false) => {
      clearTimeout(writes.get(path));
      writes.delete(path);
      if (!editor.isOpen(path)) return;
      editor.close(path);
      const shown = tabs.active === path;
      tabs.remove(path);
      if (shown) showModulesView();
      if (!removed) liveChanged();
    },
    openAccount: () => $("account-button").click(),
    // The page signed in or out by itself.
    accountChanged: async () => {
      if (cloud) cloud.user = (await cloud.request("GET", "me")).user;
      if (account) account.refresh();
    },
  });
  // The tab comes back if it was open.
  if (modules && modules.wasOpen) {
    tabs.modules = true;
    tabs.render();
  }
} catch (error) {
  console.error("The Modules tab could not start:", error);
  modules = null;
}

// Buddies, when the server offers them (buddies/panel.js).
try {
  buddies = setupBuddies({
    $, cloud, store, editor, account, notice, clearNotices, status, ask, saveAll, tree,
    project: () => project,
    show: (opened) => showProject(opened),
    isText: (path) => TEXT.test(path),
    openFile: (path) => openFile(path),
    refreshFiles: () => { if (project) tree.show(project, tree.selected); },
    // A file someone else removed.
    closeFile: (path) => {
      clearTimeout(writes.get(path));
      writes.delete(path);
      editor.close(path);
      const shown = tabs.active === path;
      tabs.remove(path);
      if (shown) {
        const next = tabs.active || tabs.paths[0] || (project && project.main);
        if (next && project.has(next)) openFile(next);
        else showHelpView();
      }
      if (project) tree.show(project, tree.selected === path ? null : tree.selected);
    },
    // The program's name, main file or mode, changed by someone else.
    metaChanged: () => {
      if (!project) return;
      $("project-name").value = project.name;
      document.title = `${project.name} - Blitz3D+ Online`;
      tree.show(project, tree.selected);
      symbolsChanged();
    },
    runFiles: (snapshot) => runFiles(snapshot),
    blip: () => blip,
    // A session began or ended here: an open World Editor's world is shared,
    // or no more.
    sessionChanged: () => syncSharedWorld(true),
  });
} catch (error) {
  console.error("Buddies could not start:", error);
  buddies = null;
}

// Which project opens: a shared program in the URL, an example the URL
// names, the project open last time, a program the Phase 1 playground kept,
// or the first example. ?example= names an example, or a program of one by
// its path from the IDE's folder, as the website's help links do:
// ?example=help/commands/2d_examples/Plot.bb.
// ?wide=1 and ?live=1 apply to the program the link opens, once: a reload
// does not ask again.
const linkAsks = { wide: params.get("wide") === "1", live: params.get("live") === "1" };
if (params.has("wide") || params.has("live")) {
  const rest = new URLSearchParams(location.search);
  rest.delete("wide");
  rest.delete("live");
  history.replaceState(null, "", location.pathname + (rest.size ? "?" + rest : "") + location.hash);
}
let opened = await openShared();
let linkOpened = opened;
if (!opened && params.get("example")) {
  const wanted = params.get("example");
  const found = wanted.includes("/") ? exampleAt(wanted) :
    { example: examples.find((candidate) => candidate.name.toLowerCase() === wanted.toLowerCase()) };
  const rest = new URLSearchParams(location.search);
  rest.delete("example");
  history.replaceState(null, "", location.pathname + (rest.size ? "?" + rest : ""));
  if (found && found.example) {
    await openExample(found.example, found.program);
    linkOpened = opened = !!project;
  } else {
    notice(`The example ${wanted.split("/").pop()} is not in the online IDE.`, [], "warning");
  }
}
// index.html drew the page full width for ?wide=1 before reading the link.
if (linkAsks.wide) {
  if (linkOpened) layout.widen();
  else layout.restore();
}
if (!opened) {
  const last = remember.get(LAST_PROJECT);
  if (last && await store.meta(last)) opened = await openProjectById(last);
}
if (!opened) {
  const draft = (() => { try { return JSON.parse(localStorage.getItem("b3d-ide:draft")); } catch { return null; } })();
  if (draft && typeof draft.source === "string" && draft.source.trim() && !(await store.list()).length) {
    const file = `${(draft.title || "Untitled").replace(/[\\/:*?"<>|]/g, "")}.bb`;
    await createProject({ name: draft.title || "Untitled", files: [[file, draft.source]], mode: draft.mode });
    try { localStorage.removeItem("b3d-ide:draft"); } catch {}
    opened = true;
  }
}
if (!opened) {
  const recent = (await store.list()).find((meta) => meta.kind === "browser");
  if (recent) opened = await openProjectById(recent.id);
}
if (!opened) await openExample(firstExample);
// The desktop World Editor's sign-in, which opened the IDE (library.js).
await library.linkFromUrl(params);

// The first build loads the compiler before the first Run.
status("Loading the compiler…");
const warmUp = await compile({ "main.bb": "" }, "main.bb", "extended", false);
$("run").disabled = !warmUp.ok;
status(warmUp.ok ? "Ready. Press Run (F5) to run the program, or F1 for help on a command." :
  "The compiler did not load.");
if (!warmUp.ok) write(warmUp.output, "error");
state.ready = warmUp.ok;
editor.focus();
if (linkOpened && linkAsks.live && state.ready) {
  // As turning Live on does, without remembering it.
  $("live").checked = live.on = true;
  run({ live: true });
}

window.b3dIdeRun = run;
window.b3dIdeStop = stop;
window.b3dIdeCompile = compile;
window.b3dIdeProject = () => project;
// Replaces the text of the file shown.
window.b3dIdeSource = (text) => {
  if (text !== undefined) editor.setText(text);
  return editor.text;
};
// Writes a file into the project.
window.b3dIdeWrite = async (path, contents) => {
  await project.write(normalisePath(path), contents);
  tree.show(project);
};
window.b3dIdeSaveAll = saveAll;
window.b3dIdeAccount = () => account;
// Opens the World Editor on a world in the project, or a new one.
window.b3dIdeWorldEditor = (path = null) => openWorldEditor(path);
// Sets the breakpoints of the file shown: rows, from 1.
window.b3dIdeBreakpoints = (rows) => {
  editor.setBreakpoints(editor.path, "", rows);
  saveBreakpoints();
  debugPane.breakpointsChanged();
};
// Sends the debugger a command line, as its buttons do.
window.b3dIdeDebugCommand = (line) => runner.debugCommand(line);
// Blip's controls, when the server offers him.
window.b3dIdeBlip = () => blip;
// Buddies' controls, when the server offers them.
window.b3dIdeBuddies = () => buddies;
// The world the World Editor shares in a buddy session, or null.
window.b3dIdeSharedWorld = () => sharedWorld ? sharedWorld.world : null;
// Sends the running program, such as the World Editor, a request through the
// page channel, and resolves with its answer (runner.js request).
window.b3dIdeRequest = (message, timeout) => runner.request(message, timeout);
